import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import { paths } from '../../../utils/constants/routes';
import { API_ENDPOINTS } from '../../../api/endpoints';
import { useEffect, useRef, useState } from 'react';
import useProjectContactsDownload from '../../../hooks/useProjectContactsDownload';
import ProjectContactsDownloadDialog from '../../../components/enrichment/ProjectContactsDownloadDialog';
import { exportEntryGate } from '../../../components/common/exportAccessTooltips';
import StaticPageLayout from '../../../components/layouts/StaticPageLayout';
import DataSourceToggleIcon from '../../../components/common/DataSourceToggleIcon';
import DataSourceFloatingLegend from '../../../components/common/DataSourceFloatingLegend';
import MobileSheet from '../../../components/common/MobileSheet';
import ResultsMobileActionBar from '../../../components/common/ResultsMobileActionBar';
import { isInsideResultsMobileBar } from '../../../utils/resultsMobileBar';
import { DATA_SOURCE, dataSourceClass } from '../../../utils/constants/dataSources';
import MobileFilters from './components/MobileFilters';
import SearchResultsBody from './components/SearchResultsBody';
import { deriveSearchResultsFlags } from './searchResultsFlags';
import {
  SEARCH_FIELDS,
  compareFields,
  getErrorFields,
  trimStateFields,
  SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA,
  FUND_SEARCH_CATEGORY_FIELDS,
  PORTFOLIO_SEARCH_CATEGORY_FIELDS,
  PROFILER_SEARCH_FIELDS,
  PROFILER_SEARCH_CATEGORY_FIELDS,
  PROFILER_INDUSTRY_CODES_SEARCH_FIELDS
} from '../../../utils/constants/fields';
import {
  getFilledSearchCategoriesCount,
  mapFieldsToState,
  formatNumber,
  getProfilerFilledSearchCategories,
  formatNumberRange,
  clearDistanceWhenCityCleared
} from '../../../utils/utils';
import { UI_OPTIONS, UI_OPTIONS_UTILS } from '../../../utils/constants/uiOptions';
import { UI_OPTION_KEYS } from '../../../utils/constants/uiOptionKeys';
import {
  PROFILER_SEARCH_CATEGORIES,
  SEARCH_CATEGORIES
} from '../../../utils/constants/searchCategories';
import { autorun, observable, runInAction } from 'mobx';
import { DETAILS_POUP_TABS } from './constants/tabs';
import DetailsPopup from './components/DetailsPopup';
import MessagePopup from './components/MessagePopup';
import { getFundsInvestmentCriteriaSearchFields } from './constants/fundsInvestmentCriteriaSearchFields';
import SaveSearchPopup from './components/SaveSearchPopup';
import useHistory from '../../../hooks/useHistory';
import useBulkCompanySearch from '../../../hooks/useBulkCompanySearch';
import { eventDownloadSearch, eventSearchResultClicked } from '../../../ga4/ga4';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../components/buttons/IconButton';
import { HEADER_MESSAGE_TYPES } from '../../../utils/constants/header';
import MassOutreachPopup from './components/MassOutreachPopup';
import agentSearchSortOptions from './constants/agentSearchSortOptions';
import profilerSearchSortOptions from './constants/profilerSearchSortOptions';
import { getPortfoliosInvestmentCriteriaSearchFields } from './constants/portfoliosInvestmentCriteriaSearchFields';
import { getProfilerCompanyCriteriaSearchFields } from './constants/profilerCompanyCriteriaSearchFields';
import CompanyDetails from '../../../components/profiler/CompanyDetails/CompanyDetails';
import {
  getEffectiveChartSort,
  isSortOptionVisibleInChart
} from '../../../components/profiler/ProfilerBarChart/chartSortDefaults';
import ProfilerSaveSearchPopup from './components/ProfilerSaveSearchPopup';
import { ACCOUNT_TYPES } from '../../../utils/constants/auth';
import DesktopFilters from './components/DesktopFilters';
import SpotlightsBar from './components/SpotlightsBar';
import ColumnFilterTrigger from './components/ColumnFilterTrigger';
import PillFilterRail from './components/PillFilterRail';
import AddFilterAnchor from './components/AddFilterAnchor';
import UnifiedChipRail from './components/UnifiedChipRail';
import { createPillFilterActions } from './constants/pillFilters';
import {
  applyExploreFilters,
  buildSpotlightContext,
  canKeepMatchesSet,
  canRemoveFilteredSet,
  computeComplement,
  getSpotlightCounts,
  hasAnyActiveFilter,
  hasEffectiveActiveFilter
} from './constants/exploreFilterEngine';
import BulkExcludeControl from './components/BulkExcludeControl';
import { SPOTLIGHT_DEFINITIONS, buildExcludedSpotlightIds } from './constants/spotlightDefinitions';
import { OPTION_KEYS } from '../../../utils/constants/optionKeys';
import { getProfilerIndustryCodesSearchFields } from './constants/profilerIndustryCodesSearchFields';
import pubSub from '../../../utils/pubsub';
import { cancelRequest } from '../../../api/api';
import {
  PAGE_SIZE as pageSize,
  snapshotReqParams,
  buildSearchRequestBody,
  handleBulkTriggerSuccess,
  applyDirectSearchResults,
  startProfilerPoll,
  markResultExcluded,
  markResultsExcluded,
  canPaginateResults
} from './getResultsHelpers';
import { runProfilerNameSearch, applyIndustriesSearchFilter } from './searchInputHelpers';
import { savedProfilerProjectUrl } from './savedProfilerProjectUrl';
import {
  saveProfilerExportFile,
  setProfilerDownloadFlags,
  isAnyProfilerDownloadInFlight,
  precheckIncludesContacts,
  PROFILER_DOWNLOAD_TYPES
} from '../../../utils/profilerDownloadHelpers';
import {
  getProfilerDownloadTriggerTooltip,
  isProfilerDownloadTriggerDisabled,
  resolveDownloadLimitReached
} from './searchResultsDownloadTrigger';
import {
  resolveSearchDescriptor,
  buildFieldsParams,
  canExecuteSearchOnEnter
} from './searchDispatchHelpers';
import {
  resolveSearchTypeAndCategory,
  resolveUrlDescriptor,
  parseSearchFieldsFromUrl,
  hydrateSearchFields,
  buildQueryFields
} from './urlParamsHelpers';
import {
  isOutreachDisabled,
  isProfilerSearchDownloadDisabled,
  isUnifiedFiltersEnabled
} from '../../../utils/constants/variables';
import { toggleIndustryCodeSelection } from '../../../utils/profiler/toggleIndustryCodeSelection';
import { collapseIndustryCodes } from '../../../utils/profiler/collapseIndustryCodes';
import { hasKnownRegisterStatus } from '../../../utils/companyStatus';
import applySelectedSort from './applySelectedSort';
import {
  getInitialProfilerColumns,
  getProfilerColumnsBySearchResultsPage,
  getProfilerExtendedFinancialsToggleBySearchResultsPage,
  getProfilerHeatmapColorCodingSelectionBySearchResultsPage,
  coerceHeatmapColorCodingForLite,
  coerceHeatmapTileSizeForLite,
  coerceProfilerColumnsForLite,
  coerceColumnFiltersForLite,
  coerceDisplayedProfilerSort,
  filterHeatmapTileSizeOptionsForAccess,
  filterHeatmapColorCodingOptionsForAccess,
  gateProfilerSortOptions,
  hasEbitMarginData,
  getProfilerHeatmapCollapsedBySearchResultsPage,
  getProfilerHeatmapGroupingToggleBySearchResultsPage,
  getProfilerHeatmapTileSizeSelectionBySearchResultsPage,
  getProfilerPreFinancialsOnlySelectionsBySearchResultsPage,
  getProfilerSortSelectionBySearchResultsPage,
  getProfilerViewSelectionBySearchResultsPage,
  hasSavedProfilerHeatmapGroupingToggleBySearchResultsPage,
  DEFAULT_PROFILER_VIEW,
  isProfilerListVisible,
  PROFILER_SEARCH_RESULTS_COLUMNS,
  PROFILER_VIEWS,
  PROFILER_VIEWS_LIST,
  saveProfilerColumnsBySearchResultsPage,
  saveProfilerExtendedFinancialsToggleBySearchResultsPage,
  saveProfilerHeatmapCollapsedBySearchResultsPage,
  saveProfilerHeatmapColorCodingSelectionBySearchResultsPage,
  saveProfilerHeatmapGroupingToggleBySearchResultsPage,
  saveProfilerHeatmapTileSizeSelectionBySearchResultsPage,
  saveProfilerPreFinancialsOnlySelectionsBySearchResultsPage,
  saveProfilerSortSelectionBySearchResultsPage,
  saveProfilerViewSelectionBySearchResultsPage,
  getActiveSpotlightsBySearchResultsPage,
  saveActiveSpotlightsBySearchResultsPage,
  getColumnFiltersBySearchResultsPage,
  saveColumnFiltersBySearchResultsPage
} from './constants/constants';
import {
  resolveFinancialsOnlySelection,
  reorderProfilerColumns
} from '../../../utils/constants/profilerColumns';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import SortableColumnItem from '../../../components/common/SortableColumnItem';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
  restrictToFirstScrollableAncestor
} from '@dnd-kit/modifiers';
import useElementHeightCssVar from '../../../hooks/useElementHeightCssvar';
import { useSearchResultsLoader } from './components/ProfilerSearchResultsLoaderNew';
import { PUNCTUATION } from '../../../utils/constants/notation';
import {
  DOWNLOAD_LIMIT_EXCEEDED_CODE,
  DOWNLOAD_LIMIT_PPTX_ONEPAGER,
  DOWNLOAD_LIMIT_XLSX_CONTACTS,
  DOWNLOAD_LIMIT_XLSX_PROFILES,
  exceedsListCap,
  listCapTooltip
} from '../../../utils/constants/downloadLimits';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import SubscriptionDownloadCounter from '../../../components/common/SubscriptionDownloadCounter';
import EnrichmentCreditCounter from '../../../components/common/EnrichmentCreditCounter';
import useSubscriptionStatus from '../../../hooks/useSubscriptionStatus';
import useBulkExportConfirm from '../../../hooks/useBulkExportConfirm';
import BulkExportConfirmModal from '../../../components/common/BulkExportConfirmModal';
import {
  clearOwnershipBasisWithoutStructures,
  isOwnershipBasisMissing,
  resolveOwnershipBasis
} from '../../../utils/ownershipCriteria';
import OwnershipBasisToggle from '../../../components/profiler/ownership/OwnershipBasisToggle';

// Header label for the first results column, by search type. A lookup rather than
// a ternary chain so adding a search type does not deepen the nesting.
const NAME_COLUMN_LABELS = {
  [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds]: 'Fund name',
  [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.']]: 'Portfolio name',
  [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Corporates]: 'Invest profile name'
};

// Download buttons show a hollow arrow while the export is running, a filled one
// once it has landed, and no icon otherwise.
const downloadStateIcon = ({ isDownloading, isSuccess }) => {
  if (isDownloading) return ICON_BUTTON_ICONS.FILE_EARMARK_ARROW_DOWN;
  if (isSuccess) return ICON_BUTTON_ICONS.FILE_EARMARK_ARROW_DOWN_FILL;
  return null;
};

const savedState = observable({
  savedScrollY: {},
  saveScrollY: (searchId = null) => {
    savedState.savedScrollY[searchId] = window.scrollY;
  }
});

const SearchResults = observer(() => {
  const { navigate, location } = useHistory();
  const {
    projectStore,
    profilerProjectStore,
    outreachStore,
    utilsStore,
    makeRequest,
    authStore,
    profilerAccessStore
  } = useStore();

  // TD-2101: enrichment users get the v3 XLSX dialog for the project contact-list
  // download; everyone else keeps the legacy immediate download (TD-1908 guard).

  const {
    downloads,
    period: contractPeriod,
    isLoading: isSubscriptionLoading,
    refetch: refetchSubscription
  } = useSubscriptionStatus();
  const isDownloadLimitReached = resolveDownloadLimitReached(downloads);
  // Bridge for the bulk-export hook (defined after useLocalObservable) so the
  // observable's onError can call into the 403 retake without re-creating state.
  const reopenForLimitExceededRef = useRef(null);

  const state = useLocalObservable(() => {
    const urlParams = new URLSearchParams(location.search);
    let searchType;
    let searchCategoryFunds;
    let searchCategoryPortfolios;
    let searchCategoryProfiler;
    let searchCategory;
    try {
      searchType = JSON.parse(decodeURIComponent(urlParams.get(SEARCH_FIELDS.SEARCH_TYPE.NAME)));
      searchCategory = urlParams.get('searchCategory');
      if (searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler && searchCategory) {
        searchCategoryProfiler = searchCategory;
      } else if (searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds && searchCategory) {
        searchCategoryFunds = searchCategory;
      } else if (
        searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'] &&
        searchCategory
      ) {
        searchCategoryPortfolios = searchCategory;
      }
    } catch {
      // w/e
    }

    if (
      ![
        UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds,
        UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'],
        UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Corporates,
        UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler,
        UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Individuals
      ].includes(searchType?.[0])
    ) {
      searchType = null;
    }

    // TD-2382: a bulk-search results URL carries only `searchCategory` (the finalize handoff does
    // not serialize the searchType/criteria fields), so without help the adjust panel's "Profile"
    // dropdown (searchType) defaults to Funds and its profiler category is left unset — "Adjust
    // search" then opens on the Funds investment-criteria form. Bulk is unambiguously a Profiler
    // search (compared against the literal token, not the picker's option set), so force both
    // the searchType and the profiler category here so the panel opens on the bulk upload field,
    // mirroring how adjusting any other search restores its own type + criteria.
    if (searchCategory === 'companyBulkSearch') {
      searchType = [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler];
      searchCategoryProfiler = searchCategory;
    }

    let isReExecute = false;
    try {
      const _isReExecute = JSON.parse(decodeURIComponent(urlParams.get('isReExecute')));
      if (_isReExecute) {
        isReExecute = _isReExecute;
      }
    } catch {
      // w/e
    }

    return {
      isRendered: true,
      unrender: () => (state.isRendered = false),
      searchType: searchType || [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds],
      loadingSearchType: searchType || [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds],
      searchCategory: searchCategory,
      isReExecute: isReExecute,
      results: [],
      fullResultsCount: 0,
      page: 1,
      pages: 1,
      searchId: null,
      loadingSearchId: true,
      paginatedSearchId: null,
      isLoading: false,
      isInitialLoading: true,
      isMobileFiltersDisplayed: false,
      toggleMobileFilters: () => {
        state.isMobileFiltersDisplayed = !state.isMobileFiltersDisplayed;
        if (state.isMobileFiltersDisplayed) {
          window.scrollTo({ top: 0 });
        }
      },
      isDesktopFiltersToggled: false,
      toggleDesktopFilters: () => {
        state.isDesktopFiltersToggled = !state.isDesktopFiltersToggled;
      },
      // F/P/I details popup [ START ]
      detailsPopupDisplayed: null,
      displayDetailsPopup: (entry) => {
        if (entry) {
          utilsStore.lockScroll(true);
          const idx = state.results.findIndex(
            (r) => (r?.id ?? r?.uuid) === (entry?.id ?? entry?.uuid)
          );
          eventSearchResultClicked({
            search_profile: state.searchType?.[0] || '',
            search_type: state.searchCategory || state.currentSearchCategory || '',
            result_type: state.searchType?.[0] || '',
            result_id: entry?.id ?? entry?.uuid ?? null,
            result_position: idx >= 0 ? idx + 1 : null
          });
        } else {
          utilsStore.lockScroll(false);
        }
        state.detailsPopupDisplayed = entry;
      },
      detailsPopupSelectedTab: DETAILS_POUP_TABS.GENERAL,
      setDetailsPopupSelectedTab: (value) => {
        state.detailsPopupSelectedTab = value;
      },
      // F/P/I details popup [  END  ]
      // Profiler company details [ START ]
      profilerCompanyDetailsDisplayed: null,
      displayProfilerCompanyDetailsDetails: (entry) => {
        if (entry) {
          savedState.saveScrollY(state.searchId);
          const idx = state.results.findIndex(
            (r) => (r?.id ?? r?.uuid) === (entry?.id ?? entry?.uuid)
          );
          eventSearchResultClicked({
            search_profile: 'corporates',
            search_type: state.searchCategory || '',
            result_type: 'company',
            result_id: entry?.id ?? entry?.uuid ?? null,
            result_position: idx >= 0 ? idx + 1 : null
          });
        } else {
          setTimeout(() => {
            const savedScrollY = savedState.savedScrollY[state.searchId] || 0;
            window.scrollTo({ top: savedScrollY });
            runInAction(() => delete savedState.savedScrollY[state.searchId]);
          }, 20);
        }
        state.profilerCompanyDetailsDisplayed = entry;
      },
      // Profiler company details [  END  ]
      // Outreach popups [ START ]
      messagePopupDisplayed: null,
      displayMessagePopup: (entry) => {
        if (entry) {
          utilsStore.lockScroll(true);
        } else {
          utilsStore.lockScroll(false);
        }
        state.messagePopupDisplayed = entry;
      },
      massOutreachPopupDisplayed: false,
      toggleMassOutreachPopup: (toggle = false) => {
        state.massOutreachPopupDisplayed = toggle;
        if (toggle) {
          utilsStore.lockScroll(true);
        } else {
          utilsStore.lockScroll(false);
        }
      },
      isAnyOutreachButtonHovered: false,
      setIsAnyOutreachButtonHovered: (newValue) => {
        state.isAnyOutreachButtonHovered = newValue;
      },
      // Outreach popups [  END  ]
      saveSearchPopupDisplayed: null,
      toggleSaveProjectPopup: (entry) => {
        state.saveSearchPopupDisplayed = entry;
        if (entry) {
          utilsStore.lockScroll(true);
        } else {
          utilsStore.lockScroll(false);
        }
      },
      saveProfilerSearchPopupDisplayed: null,
      toggleProfilerSaveProjectPopup: (entry) => {
        state.saveProfilerSearchPopupDisplayed = entry;
        if (entry) {
          utilsStore.lockScroll(true);
        } else {
          utilsStore.lockScroll(false);
        }
      },
      resultsError: null,
      failedReqParams: null,
      profilerPollInterval: null,
      getResults: (searchFields = {}, searchId = null, searchCategory) => {
        clearTimeout(state.profilerPollInterval);
        cancelRequest([
          API_ENDPOINTS.GET_SEARCH_RESULTS,
          API_ENDPOINTS.TRIGGER_PROFILER_SEARCH,
          API_ENDPOINTS.POLL_PROFILER_RESULTS,
          API_ENDPOINTS.GET_PROFILER_COMPANIES_BY_INDUSTRIES
        ]);
        state.profilerPollInterval = null;
        state.isLoading = true;
        state.resultsError = false;
        state.searchId = null;
        state.loadingSearchId = searchId;
        // TD-2344 — bulk load passes `searchFields = null` (no criteria), so
        // guard against indexing null; the bulk branch below sets its own
        // Profiler loadingSearchType.
        state.loadingSearchType = searchFields?.[SEARCH_FIELDS.SEARCH_TYPE.NAME];
        state.searchCategory = searchCategory;
        state.results = [];

        const enableProfilerGrouping = () => {
          setTimeout(() => {
            if (hasSavedProfilerHeatmapGroupingToggleBySearchResultsPage(state.searchId)) {
              return;
            }

            if (!state.isProfilerHeatmapGroupingToggled && !state.disableProfilerHeatmapGroup) {
              state.toggleProfilerHeatmapGrouping();
            }
          }, 0);
        };

        // TD-2344 — Bulk company search loads a FINALIZED result set by
        // searchId only. The BE `triggerProfilerSearch` short-circuits on a
        // `{ searchId }` body and returns the promoted user_search_results as a
        // completed, cached set (status: 'completed', isCached: true) — no
        // criteria parsing, no poll loop. We reuse the profiler grid, so hydrate
        // the same column/spotlight/view state a normal profiler search does
        // (mirrors lines ~585-602) and treat the search type as Profiler.
        // Compared against the literal token rather than UI_OPTIONS[...].Bulk so
        // recognizing a bulk search never depends on the picker's own option set.
        // (That option was non-prod only until TD-2551 made it unconditional.)
        const isBulkCompanySearch = searchCategory === 'companyBulkSearch';

        if (isBulkCompanySearch) {
          runInAction(() => {
            state.currentSearch = {};
            state.ownershipDisplayBasisOverride = null;
            state.loadingSearchType = [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler];
            state.profilerColumns = coerceProfilerColumnsForLite(
              getProfilerColumnsBySearchResultsPage(searchId) ||
                getInitialProfilerColumns(
                  state.currentSearch,
                  utilsStore.windowWidth,
                  profilerAccessStore.isProfilerLiteOnly
                ),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.isProfilerExtendedFinancialsToggled =
              getProfilerExtendedFinancialsToggleBySearchResultsPage(searchId);
            state.selectedSort = getProfilerSortSelectionBySearchResultsPage(searchId);
            state.profilerView = getProfilerViewSelectionBySearchResultsPage(searchId);
            state.activeSpotlights = new Set(getActiveSpotlightsBySearchResultsPage(searchId));
            state.columnFilters = coerceColumnFiltersForLite(
              getColumnFiltersBySearchResultsPage(searchId),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.openFilterColumn = null;
            state.profilerHeatmapTileSize = coerceHeatmapTileSizeForLite(
              getProfilerHeatmapTileSizeSelectionBySearchResultsPage(searchId),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.profilerHeatmapColorCoding = coerceHeatmapColorCodingForLite(
              getProfilerHeatmapColorCodingSelectionBySearchResultsPage(searchId),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.isProfilerHeatmapGroupingToggled =
              getProfilerHeatmapGroupingToggleBySearchResultsPage(searchId);
            state.isProfilerHeatmapCollapsed =
              getProfilerHeatmapCollapsedBySearchResultsPage(searchId);
          });

          // The bulk-finalized rows arrive in the raw profiler wire shape and must be
          // flattened before render; handleBulkTriggerSuccess owns that (see its docs).
          const bulkCtx = {
            state,
            profilerAccessStore,
            searchCategory,
            fallbackSearchId: searchId,
            enableProfilerGrouping
          };

          makeRequest({
            endpoint: API_ENDPOINTS.TRIGGER_PROFILER_SEARCH,
            body: { searchId },
            onSuccess: (payload) => handleBulkTriggerSuccess(payload, bulkCtx),
            onError: (errorMessage) => {
              if (!state.isRendered) {
                return;
              }
              state.resultsError = errorMessage || 'Failed to obtain search results';
              state.failedReqParams = snapshotReqParams(null, searchId, searchCategory);
            },
            onFinally: () => {
              if (!state.isRendered) {
                return;
              }
              state.isLoading = false;
              if (state.isInitialLoading) {
                state.isInitialLoading = false;
              }
            }
          });

          return;
        }

        const isProfilerSearch =
          state.loadingSearchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler;
        const isProfilerCompanyCriteriaSearch =
          isProfilerSearch &&
          searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'];
        const isProfilerIndustryCodesSearch =
          isProfilerSearch &&
          searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries'];

        runInAction(() => {
          state.currentSearch = JSON.parse(JSON.stringify(searchFields));
          state.ownershipDisplayBasisOverride = null;
        });

        // buildSearchRequestBody maps the criteria and applies the geo cross-field
        // rules; a geo-validation failure records the error on state and aborts.
        const { body, aborted } = buildSearchRequestBody({
          state,
          searchFields,
          searchId,
          searchCategory,
          isProfilerSearch,
          isProfilerCompanyCriteriaSearch,
          isProfilerIndustryCodesSearch
        });
        if (aborted) {
          return;
        }

        if (isProfilerSearch) {
          runInAction(() => {
            state.profilerColumns = coerceProfilerColumnsForLite(
              getProfilerColumnsBySearchResultsPage(searchId) ||
                getInitialProfilerColumns(
                  state.currentSearch,
                  utilsStore.windowWidth,
                  profilerAccessStore.isProfilerLiteOnly
                ),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.isProfilerExtendedFinancialsToggled =
              getProfilerExtendedFinancialsToggleBySearchResultsPage(searchId);
            state.selectedSort = getProfilerSortSelectionBySearchResultsPage(searchId);
            state.profilerView = getProfilerViewSelectionBySearchResultsPage(searchId);
            // TD-2200 V1 — hydrate Spotlights + column filters from localStorage.
            state.activeSpotlights = new Set(getActiveSpotlightsBySearchResultsPage(searchId));
            state.columnFilters = coerceColumnFiltersForLite(
              getColumnFiltersBySearchResultsPage(searchId),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.openFilterColumn = null;
            state.profilerHeatmapTileSize = coerceHeatmapTileSizeForLite(
              getProfilerHeatmapTileSizeSelectionBySearchResultsPage(searchId),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.profilerHeatmapColorCoding = coerceHeatmapColorCodingForLite(
              getProfilerHeatmapColorCodingSelectionBySearchResultsPage(searchId),
              profilerAccessStore.isProfilerLiteOnly
            );
            state.isProfilerHeatmapGroupingToggled =
              getProfilerHeatmapGroupingToggleBySearchResultsPage(searchId);
            state.isProfilerHeatmapCollapsed =
              getProfilerHeatmapCollapsedBySearchResultsPage(searchId);
          });
        }

        if (['number', 'string'].includes(typeof searchId)) {
          body.searchId = searchId;
        }

        body.searchCategory = searchCategory;

        makeRequest({
          endpoint: isProfilerSearch
            ? isProfilerCompanyCriteriaSearch
              ? API_ENDPOINTS.TRIGGER_PROFILER_SEARCH
              : API_ENDPOINTS.GET_PROFILER_COMPANIES_BY_INDUSTRIES
            : API_ENDPOINTS.GET_SEARCH_RESULTS,
          body,
          onSuccess: ({
            results = [],
            searchId: newSearchId,
            totalCount = 0,
            isCached = false
          }) => {
            if (!state.isRendered) {
              if (isProfilerSearch && profilerAccessStore.isProfilerDemo) {
                authStore.refreshProfilerDemoCounter();
              }
              return;
            }

            if (isProfilerCompanyCriteriaSearch) {
              if (
                profilerAccessStore.isProfilerDemo &&
                !isCached &&
                body.searchId !== newSearchId
              ) {
                authStore.userInfo.profilerSearchExecutedCount += 1;
              }

              const pollCtx = {
                state,
                profilerAccessStore,
                authStore,
                searchFields,
                searchId,
                searchCategory,
                newSearchId,
                enableProfilerGrouping
              };
              startProfilerPoll({
                makeRequest,
                newSearchId,
                immediate: body.searchId === newSearchId,
                pollCtx
              });

              return;
            }

            applyDirectSearchResults(
              { results, newSearchId, totalCount, isCached },
              {
                state,
                isProfilerSearch,
                profilerAccessStore,
                authStore,
                searchFields,
                searchCategory,
                body,
                enableProfilerGrouping
              }
            );
          },
          onError: (errorMessage) => {
            if (isProfilerSearch && profilerAccessStore.isProfilerDemo) {
              authStore.refreshProfilerDemoCounter();
            }

            if (!state.isRendered) {
              return;
            }

            state.resultsError = errorMessage || 'Failed to obtain search results';
            state.failedReqParams = snapshotReqParams(searchFields, searchId, searchCategory);

            if (isProfilerCompanyCriteriaSearch) {
              state.isLoading = false;
              if (state.isInitialLoading) {
                state.isInitialLoading = false;
              }
            }
          },
          onFinally: () => {
            if (!state.isRendered) {
              return;
            }

            if (!isProfilerCompanyCriteriaSearch) {
              state.isLoading = false;
              if (state.isInitialLoading) {
                state.isInitialLoading = false;
              }
            }
          }
        });
      },
      fundsInAction: {},
      portfoliosInAction: {},
      investProfilesInAction: {},
      currentSearchCategory: null,
      currentSearch: {},
      // The basis the Shareholders column shows; follows the search until switched.
      ownershipDisplayBasisOverride: null,
      get ownershipSearchedBasis() {
        return resolveOwnershipBasis(
          state.currentSearch?.[PROFILER_SEARCH_FIELDS.OWNERSHIP_BASIS.NAME]
        );
      },
      get ownershipDisplayBasis() {
        return state.ownershipDisplayBasisOverride || state.ownershipSearchedBasis;
      },
      setOwnershipDisplayBasis: (basis) => {
        state.ownershipDisplayBasisOverride = basis;
      },
      // funds investment criteria fields
      fundsInvestmentCriteriaFields: {
        ...mapFieldsToState(SEARCH_FIELDS),
        [SEARCH_FIELDS.SEARCH_TYPE.NAME]: searchType || [
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds
        ]
      },
      setFundsInvestmentCriteriaFieldValue: (field = {}, value) => {
        state.fundsInvestmentCriteriaFields[field.NAME] = value;
      },
      get fundsInvestmentCriteriaFieldsCount() {
        return getFilledSearchCategoriesCount(state.fundsInvestmentCriteriaFields);
      },
      fundsInvestmentCriteriaSearchSectionsToggle: Object.fromEntries(
        Object.values(SEARCH_CATEGORIES).map((section) => [section, false])
      ),
      toggleFundsInvestmentCriteriaSearchSection: (section) => {
        state.fundsInvestmentCriteriaSearchSectionsToggle[section] =
          !state.fundsInvestmentCriteriaSearchSectionsToggle[section];
        if (state.fundsInvestmentCriteriaSearchSectionsToggle[section]) {
          setTimeout(() => {
            const sectionDOM = document.getElementById(`desktop-filter-category-${section}`);
            if (sectionDOM && state.fundsInvestmentCriteriaSearchSectionsToggle[section]) {
              const parentDOM = sectionDOM.closest('.scrollable-items');

              if (parentDOM) {
                const offsetTop = sectionDOM.offsetTop - parentDOM.offsetTop;
                parentDOM.scrollTo({ behavior: 'smooth', top: offsetTop });
              }
            }
          }, 10);
        }
      },
      onFundsInvestmentCriteriaSubmitErrorState: false,
      setOnFundsInvestmentCriteriaSubmitErrorState: (value = false) =>
        (state.onFundsInvestmentCriteriaSubmitErrorState = value),
      get fundsInvestmentCriteriaValidationFields() {
        return getErrorFields(Object.values(SEARCH_FIELDS), state.fundsInvestmentCriteriaFields);
      },
      get isFundsInvestmentCriteriaSearchDisabled() {
        return (
          // state.isLoading ||
          // state.isInitialLoading ||
          !!state.fundsInvestmentCriteriaValidationFields.invalidFields.filter((f) => !f.isOnSubmit)
            .length ||
          (state.onFundsInvestmentCriteriaSubmitErrorState &&
            !!state.fundsInvestmentCriteriaValidationFields.invalidFields.length) ||
          !compareFields(state.fundsInvestmentCriteriaFields, state.currentSearch)
        );
      },
      // portfolios investment criteria fields
      portfoliosInvestmentCriteriaFields: {
        ...mapFieldsToState(SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA),
        [SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.SEARCH_TYPE.NAME]: [
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.']
        ]
      },
      setPortfoliosInvestmentCriteriaFieldValue: (field = {}, value) => {
        state.portfoliosInvestmentCriteriaFields[field.NAME] = value;
      },
      onPortfoliosInvestmentCriteriaSubmitErrorState: false,
      setOnPortfoliosInvestmentCriteriaSubmitErrorState: (value = false) =>
        (state.onPortfoliosInvestmentCriteriaSubmitErrorState = value),
      get portfoliosInvestmentCriteriaValidationFields() {
        return getErrorFields(
          Object.values(SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA),
          state.portfoliosInvestmentCriteriaFields
        );
      },
      get isPortfoliosInvestmentCriteriaSearchDisabled() {
        return (
          // state.isLoading ||
          // state.isInitialLoading ||
          !!state.portfoliosInvestmentCriteriaValidationFields.invalidFields.filter(
            (f) => !f.isOnSubmit
          ).length ||
          (state.onPortfoliosInvestmentCriteriaSubmitErrorState &&
            !!state.portfoliosInvestmentCriteriaValidationFields.invalidFields.length) ||
          !compareFields(state.portfoliosInvestmentCriteriaFields, state.currentSearch)
        );
      },
      // profiler search common state
      profilerColumns: getInitialProfilerColumns(
        null,
        utilsStore.windowWidth,
        profilerAccessStore.isProfilerLiteOnly
      ),
      isProfilerExtendedFinancialsToggled: false,
      toggleProfilerExtendedFinancials: () => {
        state.isProfilerExtendedFinancialsToggled = !state.isProfilerExtendedFinancialsToggled;
        saveProfilerExtendedFinancialsToggleBySearchResultsPage(
          state.isProfilerExtendedFinancialsToggled,
          state.searchId
        );
      },
      isProfilerCustomizeColumnsToggled: false,
      toggleProfilerCustomizeColumns: () => {
        state.isProfilerCustomizeColumnsToggled = !state.isProfilerCustomizeColumnsToggled;
      },
      isProfilerViewMenuToggled: false,
      toggleProfilerViewMenu: () => {
        state.isProfilerViewMenuToggled = !state.isProfilerViewMenuToggled;
      },
      profilerView: DEFAULT_PROFILER_VIEW,
      setProfilerView: (viewValue) => {
        state.profilerView = viewValue;
        saveProfilerViewSelectionBySearchResultsPage(viewValue, state.searchId);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);
      },
      // ====================================================================
      // TD-2200 V1 — Spotlights + column-value filtering (List view only).
      // Mutations persist to localStorage keyed by searchId. Hydration runs
      // in TWO places, both idempotent:
      //   1. Eagerly on mount (V1.9) — a small useEffect reads the URL
      //      `searchId` and restores both stores immediately, so back-nav
      //      from a detail page never drops state regardless of whether
      //      `getResults` re-fires.
      //   2. Inside the search loader's `runInAction` block (~line 540) —
      //      re-hydrates when getResults runs (initial search, re-execute,
      //      cached-results path, etc.) so a fresh search starts from the
      //      persisted state.
      // ====================================================================
      activeSpotlights: new Set(),
      columnFilters: {},
      openFilterColumn: null,
      // V1.8 — Spotlight IDs whose related column was hidden when the chip
      // was activated and got auto-un-hidden by us. On deactivation we
      // re-hide those columns to restore the analyst's prior layout. Tracked
      // in memory only (not persisted) — on reload we treat any visible
      // column as analyst-owned, which is the safer default.
      columnsAutoShownBySpotlight: new Set(),
      toggleSpotlight: (spotlightId) => {
        const wasActive = state.activeSpotlights.has(spotlightId);
        if (wasActive) {
          state.activeSpotlights.delete(spotlightId);
        } else {
          state.activeSpotlights.add(spotlightId);
        }
        saveActiveSpotlightsBySearchResultsPage([...state.activeSpotlights], state.searchId);

        // V1.7 — sync the matching column filter so the analyst sees a
        // coherent state: chip active + related column ⌄ active + popover
        // already showing the equivalent value. Spotlights without a
        // related column (High matches — AI rationale isn't filterable)
        // skip this branch entirely.
        const def = SPOTLIGHT_DEFINITIONS.find((d) => d.id === spotlightId);
        const rel = def?.relatedColumn;
        if (!rel) return;

        if (wasActive) {
          // Deactivating → clear the column filter the Spotlight had set,
          // so the column ⌄ goes back to its default state.
          state.clearColumnFilter(rel.columnKey);

          // V1.8 — symmetric un-show. If we auto-un-hid the column when
          // this Spotlight was activated, re-hide it now so the layout
          // returns to the analyst's prior state. Columns the analyst had
          // already shown (or manually toggled on after activation) are
          // NOT in the set, so they stay visible.
          if (state.columnsAutoShownBySpotlight.has(spotlightId)) {
            const col = state.profilerColumns.find((c) => c.id === rel.columnId);
            if (col) {
              col.selected = false;
              saveProfilerColumnsBySearchResultsPage(state.profilerColumns, state.searchId);
            }
            state.columnsAutoShownBySpotlight.delete(spotlightId);
          }
        } else {
          // Activating → compute the filter value (may be dynamic, e.g.
          // p75 of the loaded set for Top-quartile EBIT) and write it to
          // the column filter as if the analyst had set it via the popover.
          const base = state.results.filter((c) => !c.isExcluded);
          const filterValue = rel.computeFilterValue(base);
          state.setColumnFilter(rel.columnKey, filterValue);

          // If the related column is currently hidden via Customize
          // Columns, un-hide it so the filter has visible feedback in the
          // result rows. Record that we did so, so the V1.8 deactivation
          // branch above can re-hide it later.
          const col = state.profilerColumns.find((c) => c.id === rel.columnId);
          if (col?.selected === false) {
            col.selected = true;
            saveProfilerColumnsBySearchResultsPage(state.profilerColumns, state.searchId);
            state.columnsAutoShownBySpotlight.add(spotlightId);
          }
        }
      },
      setColumnFilter: (columnKey, filterValue) => {
        if (filterValue == null) {
          delete state.columnFilters[columnKey];
        } else {
          state.columnFilters[columnKey] = filterValue;
        }
        // Replace the object reference so any non-deep observers re-evaluate.
        state.columnFilters = { ...state.columnFilters };
        saveColumnFiltersBySearchResultsPage(state.columnFilters, state.searchId);
      },
      // TD-2456 — clickable-pill store actions (togglePillFilter /
      // setPillFilterMode), shared with the Projects results view via
      // createPillFilterActions so the toggle logic isn't duplicated. The toggle
      // matches the clicked pill case-insensitively (normalizeKeywordQuery) so a
      // lit case-variant pill clears instead of appending a near-duplicate. The
      // chip-rail subset + clear-all live in the shared PillFilterRail.
      ...createPillFilterActions(() => state, {
        // Any/OR is the unified model's default (TD-2561); the current pill UI
        // ships All/AND, so the flip arrives with the flag that introduces it.
        defaultMode: isUnifiedFiltersEnabled ? 'or' : 'and'
      }),
      clearColumnFilter: (columnKey) => {
        if (!(columnKey in state.columnFilters)) return;
        delete state.columnFilters[columnKey];
        state.columnFilters = { ...state.columnFilters };
        saveColumnFiltersBySearchResultsPage(state.columnFilters, state.searchId);
      },
      clearAllFilters: () => {
        state.activeSpotlights.clear();
        state.columnFilters = {};
        state.openFilterColumn = null;
        // Forget spotlight-auto-shown column tracking; leaving those columns
        // visible is the safer default (the analyst can re-hide via Customize).
        state.columnsAutoShownBySpotlight.clear();
        saveActiveSpotlightsBySearchResultsPage([], state.searchId);
        saveColumnFiltersBySearchResultsPage({}, state.searchId);
      },
      setOpenFilterColumn: (columnKey) => {
        state.openFilterColumn = columnKey;
      },
      // Unified-filter model — Add-filter panel UI state (not persisted).
      isAddFilterPanelOpen: false,
      addFilterDimension: null,
      openAddFilterPanel: (dimensionKey = null) => {
        if (dimensionKey) state.addFilterDimension = dimensionKey;
        state.isAddFilterPanelOpen = true;
        // Never run the old per-column popover alongside the panel.
        state.openFilterColumn = null;
      },
      closeAddFilterPanel: () => {
        state.isAddFilterPanelOpen = false;
      },
      setAddFilterDimension: (dimensionKey) => {
        state.addFilterDimension = dimensionKey;
      },
      get categoryExcludedSpotlightIds() {
        // Spotlights whose predicate can't apply to the current context.
        // TD-2898 — one shared rule with the project view, which derived this
        // inline and so never picked up TD-2499's EBIT branch. Passing
        // `hasEbitMarginData` as well keeps the chip in step with this store's
        // own sort menu (TD-2890), which is already keyed on the data.
        return buildExcludedSpotlightIds({
          searchCategory: state.searchCategory,
          isProfilerLiteOnly: profilerAccessStore.isProfilerLiteOnly,
          hasEbitData: state.hasEbitMarginData
        });
      },
      get filteredResults() {
        // Apply Spotlights ∩ column filters to the displayable (non-excluded)
        // set. `sortedResults` is intentionally left untouched so Heatmap /
        // Chart / downloads keep their current unfiltered behavior per brief.
        const base = state.results.filter((entry) => !entry.isExcluded);
        const ctx = buildSpotlightContext(base);
        // Drop Spotlights hidden for this search category — a stale / inherited
        // active ID must not silently filter the list to empty with no chip.
        const excluded = state.categoryExcludedSpotlightIds;
        const activeForFilter = excluded
          ? new Set([...state.activeSpotlights].filter((id) => !excluded.has(id)))
          : state.activeSpotlights;
        return applyExploreFilters(base, activeForFilter, state.columnFilters, ctx);
      },
      get spotlightCounts() {
        // Honest live counts: each Spotlight count reflects "what this
        // Spotlight would add to the current column-filter set," computed
        // against the non-excluded results minus active spotlights.
        const base = state.results.filter((entry) => !entry.isExcluded);
        return getSpotlightCounts(base, state.columnFilters);
      },
      // Bridge the global Profiler-Lite role onto the results store so the shared
      // Add-filter panel (also mounted by the Projects store) can hide the
      // EBIT-margin dimension for Lite without reaching for the root store.
      get isProfilerLiteOnly() {
        return profilerAccessStore.isProfilerLiteOnly;
      },
      profilerHeatmapTileSize: UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE].Sales,
      setProfilerHeatmapTileSize: (newValue) => {
        state.profilerHeatmapTileSize = newValue;
        saveProfilerHeatmapTileSizeSelectionBySearchResultsPage(newValue, state.searchId);
      },
      isProfilerHeatmapTileSizeMenuToggled: false,
      toggleProfilerHeatmapTileSizeMenu: () => {
        state.isProfilerHeatmapTileSizeMenuToggled = !state.isProfilerHeatmapTileSizeMenuToggled;
      },
      get profilerHeatmapTileSizeOptions() {
        return filterHeatmapTileSizeOptionsForAccess(
          UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_SIZE],
          !profilerAccessStore.isProfilerLiteOnly
        );
      },
      // TD-2499: Lite default colour-coding is CAGR 3Y (no EBIT margin); Full keeps EBIT Margin.
      profilerHeatmapColorCoding: profilerAccessStore.isProfilerLiteOnly
        ? UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['CAGR 3Y (%)']
        : UI_OPTIONS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING]['EBIT Margin (%)'],
      setProfilerHeatmapColorCoding: (newValue) => {
        state.profilerHeatmapColorCoding = newValue;
        saveProfilerHeatmapColorCodingSelectionBySearchResultsPage(newValue, state.searchId);
      },
      isProfilerHeatmapColorCodingMenuToggled: false,
      toggleProfilerHeatmapColorCodingMenu: () => {
        state.isProfilerHeatmapColorCodingMenuToggled =
          !state.isProfilerHeatmapColorCodingMenuToggled;
      },
      get profilerHeatmapColorCodingOptions() {
        return filterHeatmapColorCodingOptionsForAccess(
          UI_OPTIONS_UTILS[UI_OPTION_KEYS.PROFILER_HEATMAP_COLOR_CODING],
          !profilerAccessStore.isProfilerLiteOnly
        );
      },
      // TD-2720 — the heatmap sits on top of the list, so it can be folded away to
      // give the list the whole viewport. Persisted per search (see the helper in
      // constants.js) alongside the view / tile-size / colour-coding selections.
      isProfilerHeatmapCollapsed: false,
      toggleProfilerHeatmapCollapsed: () => {
        state.isProfilerHeatmapCollapsed = !state.isProfilerHeatmapCollapsed;
        saveProfilerHeatmapCollapsedBySearchResultsPage(
          state.isProfilerHeatmapCollapsed,
          state.searchId
        );
      },
      isProfilerHeatmapGroupingToggled: false,
      toggleProfilerHeatmapGrouping: () => {
        state.isProfilerHeatmapGroupingToggled = !state.isProfilerHeatmapGroupingToggled;
        saveProfilerHeatmapGroupingToggleBySearchResultsPage(
          state.isProfilerHeatmapGroupingToggled,
          state.searchId
        );
        state.toggleProfilerHeatmapGroupingCallback(state.isProfilerHeatmapGroupingToggled);
      },
      toggleProfilerHeatmapGroupingCallback: () => {},
      setToggleProfilerHeatmapGroupingCallback: (func = () => {}) => {
        runInAction(() => {
          state.toggleProfilerHeatmapGroupingCallback = func;
        });
      },
      get profilerHeatmapGroup() {
        if (!state.isProfilerHeatmapGroupingToggled || state.disableProfilerHeatmapGroup) {
          return null;
        }

        let group = null;
        if (
          state.searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']
        ) {
          group = '_industryCode';
        } else if (
          state.searchCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Characteristics
        ) {
          group = 'scoreLabel';
        }

        return group;
      },
      get disableProfilerHeatmapGroup() {
        let group = null;
        if (
          state.searchCategory === UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Industries']
        ) {
          group = '_industryCode';
        } else if (
          state.searchCategory ===
          UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY].Characteristics
        ) {
          group = 'scoreLabel';
        }

        if (!group) {
          return true;
        }

        return new Set(state.sortedResults.map((company) => company[group])).size < 2;
      },
      // profiler company criteria fields
      profilerCompanyCriteriaFields: {
        ...mapFieldsToState(PROFILER_SEARCH_FIELDS),
        [PROFILER_SEARCH_FIELDS.SEARCH_TYPE.NAME]: [UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler]
      },
      setProfilerCompanyCriteriaFieldValue: (field = {}, value) => {
        state.profilerCompanyCriteriaFields[field.NAME] = value;
        if (
          !state.profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME] &&
          !state.profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME]
        ) {
          state.profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME] = [];
        }
        clearOwnershipBasisWithoutStructures(state.profilerCompanyCriteriaFields);

        // If City/Zipcode is cleared, clear distance too
        clearDistanceWhenCityCleared(
          state.profilerCompanyCriteriaFields,
          field,
          value,
          PROFILER_SEARCH_FIELDS.CITY_ZIPCODE,
          PROFILER_SEARCH_FIELDS.DISTANCE
        );
      },
      get profilerCompanyCriteriaFieldsCount() {
        return getProfilerFilledSearchCategories(state.profilerCompanyCriteriaFields);
      },
      profilerCompanyCriteriaDearchSectionsToggle: Object.fromEntries(
        Object.values(PROFILER_SEARCH_CATEGORIES).map((section) => [section, false])
      ),
      toggleProfilerCompanyCriteriaSearchSection: (section) => {
        state.profilerCompanyCriteriaDearchSectionsToggle[section] =
          !state.profilerCompanyCriteriaDearchSectionsToggle[section];
        if (state.profilerCompanyCriteriaDearchSectionsToggle[section]) {
          setTimeout(() => {
            const sectionDOM = document.getElementById(`desktop-filter-category-${section}`);
            if (sectionDOM && state.profilerCompanyCriteriaDearchSectionsToggle[section]) {
              const parentDOM = sectionDOM.closest('.scrollable-items');

              if (parentDOM) {
                const offsetTop = sectionDOM.offsetTop - parentDOM.offsetTop;
                parentDOM.scrollTo({ behavior: 'smooth', top: offsetTop });
              }
            }
          }, 10);
        }
      },
      onProfilerCompanyCriteriaSubmitErrorState: false,
      setOnProfilerCompanyCriteriaSubmitErrorState: (value = false) =>
        (state.onProfilerCompanyCriteriaSubmitErrorState = value),
      get profilerCompanyCriteriaValidationFields() {
        return getErrorFields(
          Object.values(PROFILER_SEARCH_FIELDS),
          state.profilerCompanyCriteriaFields
        );
      },
      get isProfilerCompanyCriteriaSearchDisabled() {
        return (
          // state.isLoading ||
          // state.isInitialLoading ||
          !!state.profilerCompanyCriteriaValidationFields.invalidFields.filter((f) => !f.isOnSubmit)
            .length ||
          (state.onProfilerCompanyCriteriaSubmitErrorState &&
            !!state.profilerCompanyCriteriaValidationFields.invalidFields.length) ||
          !compareFields(state.profilerCompanyCriteriaFields, state.currentSearch) ||
          state.isProfilerCompanyCriteriaSearchFieldsDisabled ||
          (state.profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME] &&
            !state.profilerCompanyCriteriaFields[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME].length) ||
          isOwnershipBasisMissing(state.profilerCompanyCriteriaFields)
        );
      },
      get isProfilerCompanyCriteriaSearchFieldsDisabled() {
        return profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit;
      },
      // profiler industry codes fields
      profilerIndustryCodesFields: {
        ...mapFieldsToState(PROFILER_INDUSTRY_CODES_SEARCH_FIELDS),
        [PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.SEARCH_TYPE.NAME]: [
          UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
        ]
      },
      setProfilerIndustryCodesFieldValue: (field = {}, value) => {
        state.profilerIndustryCodesFields[field.NAME] = value;
        if (
          !state.profilerIndustryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME] &&
          !state.profilerIndustryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MAX.NAME]
        ) {
          state.profilerIndustryCodesFields[
            PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.NAME
          ] = [];
        }
        clearOwnershipBasisWithoutStructures(state.profilerIndustryCodesFields);

        // If City/Zipcode is cleared, clear distance too
        clearDistanceWhenCityCleared(
          state.profilerIndustryCodesFields,
          field,
          value,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CITY_ZIPCODE,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.DISTANCE
        );
      },
      onProfilerIndustryCodesSubmitErrorState: false,
      setOnProfilerIndustryCodesSubmitErrorState: (value = false) =>
        (state.onProfilerIndustryCodesSubmitErrorState = value),
      get profilerIndustryCodesValidationFields() {
        return getErrorFields(
          Object.values(PROFILER_INDUSTRY_CODES_SEARCH_FIELDS),
          state.profilerIndustryCodesFields
        );
      },
      get isProfilerIndustryCodesSearchDisabled() {
        return (
          // state.isLoading ||
          // state.isInitialLoading ||
          !!state.profilerIndustryCodesValidationFields.invalidFields.filter((f) => !f.isOnSubmit)
            .length ||
          (state.onProfilerIndustryCodesSubmitErrorState &&
            !!state.profilerIndustryCodesValidationFields.invalidFields.length) ||
          !compareFields(state.profilerIndustryCodesFields, state.currentSearch) ||
          state.isProfilerIndustryCodesSearchFieldsDisabled ||
          (state.profilerIndustryCodesFields[PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_MIN.NAME] &&
            !state.profilerIndustryCodesFields[
              PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.CAGR_PERIOD.NAME
            ].length) ||
          isOwnershipBasisMissing(state.profilerIndustryCodesFields)
        );
      },
      get isProfilerIndustryCodesSearchFieldsDisabled() {
        return profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit;
      },
      profilerIndustriesListFromURL: [],
      profilerIndustriesSearchTreeToggled: false,
      toggleProfilerIndustriesSearchTree: () => {
        state.profilerIndustriesSearchTreeToggled = !state.profilerIndustriesSearchTreeToggled;
        if (!state.profilerIndustriesSearchTreeToggled) {
          state.setprofilerIndustriesSearchSearchKey('');
        }
      },
      profilerIndustriesSearchIsTyping: false,
      profilerIndustriesSearchSearchKey: '',
      setprofilerIndustriesSearchSearchKey: (value = '') => {
        state.profilerIndustriesSearchIsTyping = true;
        state.profilerIndustriesSearchSearchKey = value;
        clearTimeout(state.profilerIndustriesSearchInputTimeout);
        if (value.length < 1) {
          return;
        }

        const firedSearchKey = value;
        // NOTE: assigns the NAME-search timer slot, not the industries one. That
        // is pre-existing (the clearTimeout above targets the industries slot) and
        // deliberately left alone here — see the follow-up ticket.
        state.profilerNameSearchInputTimeout = setTimeout(
          () => applyIndustriesSearchFilter({ state, firedSearchKey }),
          500
        );
      },
      profilerIndustriesSearchInputTimeout: null,
      industryCodesFlat: [],
      industryCodesRaw: [],
      toggleIndustryCode: (code) => {
        const fromFlat = state.industryCodesFlat.find((_c) => _c.value === code.value);
        runInAction(() => {
          fromFlat.toggled = !fromFlat.toggled;
        });
      },
      selectIndustryCode: (code) => {
        runInAction(() => {
          toggleIndustryCodeSelection(state.industryCodesFlat, code);
        });
      },
      toggleAllIndustryCodes: () => {
        runInAction(() => {
          state.industryCodesFlat.forEach((code) => {
            if (code.children?.length) {
              code.toggled = true;
            }
          });
        });
      },
      untoggleAllIndustryCodes: () => {
        runInAction(() => {
          state.industryCodesFlat.forEach((code) => {
            code.toggled = false;
          });
        });
      },
      clearAllIndustryCodes: () => {
        runInAction(() => {
          state.industryCodesFlat.forEach((code) => {
            code.selected = false;
          });
        });
      },
      // profiler name search state
      profilerNameSearchIsTyping: false,
      profilerNameSearchSearchKey: '',
      setprofilerNameSearchSearchKey: (value = '') => {
        state.profilerNameSearchIsTyping = true;
        state.profilerNameSearchSearchKey = value;
        clearTimeout(state.profilerNameSearchInputTimeout);
        state.profilerNameSearchIsLoading = false;
        state.profilerNameSearchSearchKeyResults = [];
        if (value.trim().length < 2) {
          return;
        }

        const firedSearchKey = value.trim();
        state.profilerNameSearchInputTimeout = setTimeout(
          () => runProfilerNameSearch({ state, firedSearchKey, makeRequest }),
          1_000
        );
      },
      profilerNameSearchClearSearchKey: () => {
        state.profilerNameSearchSearchKey = '';
        state.profilerNameSearchIsTyping = false;
        state.profilerNameSearchIsLoading = false;
        clearTimeout(state.profilerNameSearchInputTimeout);
        state.profilerNameSearchLoadError = null;
        state.profilerNameSearchSearchKeyResults = [];
      },
      profilerNameSearchInputTimeout: null,
      profilerNameSearchIsLoading: false,
      profilerNameSearchSearchKeyResults: [],
      profilerNameSearchLoadError: null,
      profilerNameSearchIsLoadingCompanyData: false,
      profilerNameSearchLoadCompanyData: (companyId = '', onErrorCallback = () => {}) => {
        state.profilerNameSearchIsLoadingCompanyData = true;
        makeRequest({
          endpoint: API_ENDPOINTS.GET_PROFILER_COMPANY_FROM_SEARCH,
          body: { companyId, searchKey: state.profilerNameSearchSearchKey },
          onSuccess: ({ companyData, isCached }) => {
            state.profilerNameSearchSelectedCompany = companyData;
            if (profilerAccessStore.isProfilerDemo && !isCached) {
              authStore.userInfo.profilerSearchExecutedCount += 1;
            }
            // TD-2376: reconcile from the BE on success so the demo counter
            // matches the authoritative count without waiting for an F5.
            // Follow-up (TD-2376, to discuss): also fires for cached loads
            // (isCached), which consume no credit — a harmless extra GET; could
            // gate the refetch on !isCached later.
            if (profilerAccessStore.isProfilerDemo) {
              authStore.refreshProfilerDemoCounter();
            }
          },
          onError: (errorMessage) => {
            utilsStore.setHeaderMessage(
              errorMessage || 'Failed to load company data',
              HEADER_MESSAGE_TYPES.ERROR
            );
            if (profilerAccessStore.isProfilerDemo) {
              authStore.refreshProfilerDemoCounter();
            }
            onErrorCallback(errorMessage);
          },
          onFinally: () => {
            state.profilerNameSearchIsLoadingCompanyData = false;
          }
        });
      },
      profilerNameSearchSelectedCompany: null,
      //
      isSavingSearch: false,
      isProfilerSavingSearch: false,
      get projectId() {
        return (
          state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
            ? profilerProjectStore.allProjects
            : projectStore.allProjects
        ).find((p) => p.searchId === state.searchId)?.id;
      },
      onSearch: (e) => {
        e?.preventDefault?.();

        // The (searchType × searchCategory) dispatch matrix lives in
        // searchDispatchHelpers.js — resolve the current combination to its
        // descriptor, then run the one shared linear body (TD-2377).
        const descriptor = resolveSearchDescriptor(state);
        if (!descriptor) {
          return;
        }

        const onSuccessfullyFiredSearch = () => {
          document.getElementsByClassName('page-search-listing')[0].scrollTo({ top: 0 });
          if (state.isDesktopFiltersToggled) {
            state.toggleDesktopFilters();
          }
          if (state.isMobileFiltersDisplayed) {
            state.toggleMobileFilters();
          }
        };

        trimStateFields(state[descriptor.criteriaFieldsKey]);
        if (state[descriptor.validationFieldsKey].invalidFields.length) {
          if (!state[descriptor.errorStateKey]) {
            state[descriptor.setErrorStateAction](true);
          }
          return;
        }

        if (descriptor.resetSort) {
          state.selectedSort = 'default';
        }

        const fieldsParams = buildFieldsParams(state[descriptor.criteriaFieldsKey]);
        navigate(
          paths.SEARCH_RESULTS +
            `?searchCategory=${encodeURIComponent(descriptor.searchCategory)}&page=1&${fieldsParams}`
        );

        onSuccessfullyFiredSearch();
      },
      onSubmitMessage: (id, subject, message, documents, onSuccess, onError) => {
        const isFund = state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds;
        const isPortfolio =
          state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.'];
        const actionObjKey = isFund
          ? 'fundsInAction'
          : isPortfolio
            ? 'portfoliosInAction'
            : 'investProfilesInAction';
        const actionObj = state[actionObjKey];

        actionObj[id] = true;
        outreachStore.sendOutreach(
          state.projectId,
          [id],
          subject,
          message,
          documents,
          onSuccess,
          onError,
          () => {
            actionObj[id] = false;
          }
        );
      },
      onMassOutreach: (ids = [], subject, message, documents, onSuccess, onError) => {
        outreachStore.sendOutreach(
          state.projectId,
          ids,
          subject,
          message,
          documents,
          onSuccess,
          onError
        );
      },
      onSaveSearch: (searchName, searchReason, onSuccess, onError, onFinally) => {
        state.isSavingSearch = true;
        projectStore.saveProject(
          state.searchId,
          searchName,
          searchReason,
          onSuccess,
          onError,
          () => {
            state.isSavingSearch = false;
            onFinally();
          }
        );
      },
      onProfilerSaveSearch: (searchName, searchReason, onSuccess, onError, onFinally) => {
        state.isProfilerSavingSearch = true;
        profilerProjectStore.saveProject(
          state.searchId,
          searchName,
          searchReason,
          onSuccess,
          onError,
          () => {
            state.isProfilerSavingSearch = false;
            onFinally();
          }
        );
      },
      excludingResults: {},
      excludeResultQueue: [],
      getIsExcludingResult: (resultId) => {
        return !!state.excludingResults[resultId];
      },
      onExcludeResult: (entry, exclude = true) => {
        runInAction(() => {
          state.excludingResults[entry.id] = true;
        });

        const task = () =>
          makeRequest({
            endpoint:
              state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
                ? API_ENDPOINTS.EXCLUDE_PROFILER_PROJECT_RESULT
                : API_ENDPOINTS.EXCLUDE_PROJECT_RESULT,
            body: {
              ...(state.projectId ? { projectId: state.projectId } : { searchId: state.searchId }),
              ...(state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
                ? { companyId: entry.id }
                : { resultId: entry.id }),
              exclude
            },
            onSuccess: () => markResultExcluded(state, entry.id, exclude),
            onError: (errorMessage) => {
              utilsStore.setHeaderMessage(
                errorMessage ||
                  `Failed to ${exclude ? 'exclude' : 'restore'} result with name ${entry.name}.`,
                HEADER_MESSAGE_TYPES.ERROR
              );
            },
            onFinally: () => {
              state.excludingResults[entry.id] = false;
              state.dequeueExcludeResultTask();
            }
          });

        state.excludeResultQueue.push(task);
        if (state.excludeResultQueue.length === 1) {
          task();
        }
      },
      dequeueExcludeResultTask: () => {
        state.excludeResultQueue.shift();
        if (state.excludeResultQueue.length > 0) {
          state.excludeResultQueue[0]();
        }
      },
      // remove when API_ENDPOINTS.EXCLUDE_PROFILER_PROJECT_RESULT and API_ENDPOINTS.EXCLUDE_PROJECT_RESULT are fixed [  END  ]

      // TD-2461 — bulk "mass exclusion" over the current filter set. One
      // dropdown, two directions: Remove filtered (exclude the matching set F)
      // and Keep matches (exclude the complement A\F). Excludes the target set
      // in a SINGLE request via TD-2462's bulk `companyIds[]` endpoint.
      isBulkExcludeMenuToggled: false,
      massExcludeInFlight: false,
      toggleBulkExcludeMenu: (force) => {
        state.isBulkExcludeMenuToggled =
          typeof force === 'boolean' ? force : !state.isBulkExcludeMenuToggled;
      },
      get keptResults() {
        // The rows matching the active filters (== the displayed list).
        return state.filteredResults;
      },
      get complementResults() {
        // The non-excluded loaded rows that do NOT match the active filters.
        const base = state.results.filter((entry) => !entry.isExcluded);
        return computeComplement(base, state.filteredResults);
      },
      get keptCount() {
        return state.keptResults.length;
      },
      get complementCount() {
        return state.complementResults.length;
      },
      get hasEffectiveFilter() {
        return hasEffectiveActiveFilter(
          state.activeSpotlights,
          state.columnFilters,
          state.categoryExcludedSpotlightIds
        );
      },
      get isBulkExcluding() {
        return (
          state.massExcludeInFlight ||
          state.excludeResultQueue.length > 0 ||
          Object.values(state.excludingResults).some(Boolean)
        );
      },
      get canRemoveFiltered() {
        return canRemoveFilteredSet({
          hasEffectiveFilter: state.hasEffectiveFilter,
          keptCount: state.keptCount,
          isBulkExcluding: state.isBulkExcluding
        });
      },
      get canKeepMatches() {
        return canKeepMatchesSet({
          hasEffectiveFilter: state.hasEffectiveFilter,
          keptCount: state.keptCount,
          complementCount: state.complementCount,
          isBulkExcluding: state.isBulkExcluding
        });
      },
      excludeMatching: () => {
        if (!state.canRemoveFiltered) return;
        // Snapshot before dispatching — each success flips `isExcluded`, which
        // recomputes `keptResults` and would shrink the target mid-loop.
        state._excludeEntries(state.keptResults.slice());
      },
      excludeComplement: () => {
        if (!state.canKeepMatches) return;
        state._excludeEntries(state.complementResults.slice());
      },
      // Exclude the whole target set in ONE request via TD-2462's bulk
      // `companyIds[]` endpoint (backward-compatible with single-company
      // exclude). No per-row fan-out: one round-trip, then flip `isExcluded`
      // on every affected row so they move into the "Excluded results" panel.
      _excludeEntries: (entries) => {
        // TD-2626 E: bulk UI is Profiler-gated today; do not post companyIds to
        // the Profiler endpoint for Funds/etc. (single-row onExcludeResult branches).
        // Match surrounding UI gates: loadingSearchType during a type switch.
        const currentSearchType = state.loadingSearchType?.[0] || state.searchType?.[0];
        if (currentSearchType !== UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler) return;
        if (!entries.length) return;
        const companyIds = entries.map((entry) => entry.id);
        runInAction(() => {
          state.massExcludeInFlight = true;
        });
        makeRequest({
          endpoint: API_ENDPOINTS.EXCLUDE_PROFILER_PROJECT_RESULT,
          body: {
            ...(state.projectId ? { projectId: state.projectId } : { searchId: state.searchId }),
            companyIds,
            exclude: true
          },
          onSuccess: () => {
            runInAction(() => markResultsExcluded(state, companyIds));
          },
          onError: (errorMessage) => {
            utilsStore.setHeaderMessage(
              errorMessage || `Failed to exclude ${companyIds.length} companies.`,
              HEADER_MESSAGE_TYPES.ERROR
            );
          },
          onFinally: () => {
            runInAction(() => {
              state.massExcludeInFlight = false;
            });
          }
        });
      },
      isExcludedExpanded: false,
      toggleExcludedExpanded: () => (state.isExcludedExpanded = !state.isExcludedExpanded),
      uiFormStateFundsInvestmentCriteria: {
        isEBITDAFieldFocused: false,
        isEBITDAMarginFieldFocused: false,
        get hasRevenueValue() {
          return !!state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.REVENUE.NAME]?.length;
        },
        get isEBITDAFieldDisabled() {
          return (
            !state.uiFormStateFundsInvestmentCriteria.hasRevenueValue ||
            state.uiFormStateFundsInvestmentCriteria.isEBITDAMarginFieldFocused
          );
        },
        get isEBITDAMarginFieldDisabled() {
          return (
            !state.uiFormStateFundsInvestmentCriteria.hasRevenueValue ||
            state.uiFormStateFundsInvestmentCriteria.isEBITDAFieldFocused
          );
        },
        onRevenueChange: (revenue) => {
          const hasValue = !!revenue?.length;
          if (hasValue) {
            const EBITDAValue = state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.EBITDA.NAME];
            const EBITDAMarginValue =
              state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.EBITDA_MARGIN.NAME];
            if (EBITDAValue) {
              state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.EBITDA_MARGIN.NAME] = formatNumber(
                ((+EBITDAValue / +revenue) * 100).toFixed(2),
                ''
              );
            } else if (EBITDAMarginValue) {
              state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.EBITDA.NAME] = formatNumber(
                ((+revenue * +EBITDAMarginValue) / 100).toFixed(2),
                ''
              );
            }
          }
        },
        onEBITDAChange: (EBITDA) => {
          const hasValue = !!EBITDA?.length;
          if (hasValue) {
            const revenue = state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.REVENUE.NAME];
            state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.EBITDA_MARGIN.NAME] = formatNumber(
              ((+EBITDA / +revenue) * 100).toFixed(2),
              ''
            );
          }
        },
        onEBITDAFocus: () => {
          state.uiFormStateFundsInvestmentCriteria.isEBITDAFieldFocused = true;
        },
        onEBITDABlur: () => {
          state.uiFormStateFundsInvestmentCriteria.isEBITDAFieldFocused = false;
        },
        onEBITDAMarginChange: (EBITDAMargin) => {
          const hasValue = !!EBITDAMargin?.length;
          if (hasValue) {
            const revenue = state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.REVENUE.NAME];
            state.fundsInvestmentCriteriaFields[SEARCH_FIELDS.EBITDA.NAME] = formatNumber(
              ((+revenue * +EBITDAMargin) / 100).toFixed(2),
              ''
            );
          }
        },
        onEBITDAMarginFocus: () => {
          state.uiFormStateFundsInvestmentCriteria.isEBITDAMarginFieldFocused = true;
        },
        onEBITDAMarginBlur: () => {
          state.uiFormStateFundsInvestmentCriteria.isEBITDAMarginFieldFocused = false;
        }
      },
      selectedSort: 'default',
      selectSortOption: (value = '', isProfiler) => {
        state.selectedSort = value;
        if (isProfiler) {
          saveProfilerSortSelectionBySearchResultsPage(state.selectedSort, state.searchId);
        }
        const fieldsParams = Object.entries(state.currentSearch)
          .map(([k, v]) => `${[k]}=${encodeURIComponent(JSON.stringify(v))}`)
          .join('&');
        navigate(
          paths.SEARCH_RESULTS +
            `?searchCategory=${state.searchCategory}&page=${1}&${
              state.searchId ? `searchId=${state.searchId}&` : ''
            }${fieldsParams}`
        );
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
      },
      isSortToggled: false,
      toggleSort: () => (state.isSortToggled = !state.isSortToggled),
      get agentSortOptions() {
        return agentSearchSortOptions.filter(
          (o) => o.onlyForSearchType === state.searchType?.[0] || o.onlyForSearchType === 'all'
        );
      },
      get profilerSortOptions() {
        const options = state.currentSearchCategory
          ? profilerSearchSortOptions.filter((o) =>
              o.onlyForSearchCategory
                ? o.onlyForSearchCategory === state.currentSearchCategory
                : true
            )
          : profilerSearchSortOptions;

        // TD-2890 — keyed on the data, not the tier: the BE omits the EBIT block from
        // accounts without EBIT entitlement, so an empty set is the answer. Gated here
        // rather than in the menu's column filter, which the desktop menu bypasses below
        // 1366px and so left the options offered between 834 and 1365px.
        return gateProfilerSortOptions(options, {
          hasEbitData: state.hasEbitMarginData,
          hasRegisterStatusData: state.hasRegisterStatusData
        });
      },
      // The sort the results are really ordered by: the Chart view falls back to
      // EBIT-margin ↓ while nothing is chosen. Display only — applying and persisting a
      // sort stays on `selectedSort`, so List and Heatmap keep relevance order.
      get hasEbitMarginData() {
        return hasEbitMarginData(state.results);
      },
      get hasRegisterStatusData() {
        return hasKnownRegisterStatus(state.results);
      },
      get effectiveSort() {
        return coerceDisplayedProfilerSort(
          getEffectiveChartSort(
            state.selectedSort,
            state.profilerView === PROFILER_VIEWS_LIST[2].value
          ),
          {
            hasEbitData: state.hasEbitMarginData,
            hasRegisterStatusData: state.hasRegisterStatusData
          }
        );
      },
      get sortedResults() {
        // Unfiltered (non-excluded) + sorted. Consumed by Heatmap, Chart,
        // download-limit gates, pagination header count, etc. Preserves
        // pre-TD-2200 behavior — Spotlights and column filters never narrow
        // this list (see `filteredAndSortedResults` for the List-view input).
        return applySelectedSort(
          state.results.slice().filter((entry) => !entry.isExcluded),
          state.selectedSort
        );
      },
      get filteredAndSortedResults() {
        // TD-2200 V1 — feeds the List-view row slice and pagination page
        // count only. Heatmap / Chart / downloads keep reading
        // `sortedResults` so their data set stays unfiltered per brief.
        return applySelectedSort(state.filteredResults.slice(), state.selectedSort);
      },
      get fundsCategories() {
        return Object.entries(UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]).map(
          ([name, value]) => ({
            name,
            value,
            disabled:
              value !== UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria']
          })
        );
      },
      get portfoliosCategories() {
        return Object.entries(UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]).map(
          ([name, value]) => ({
            name,
            value,
            disabled:
              value !== UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria']
          })
        );
      },
      get profilerCategories() {
        return Object.entries(UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]).map(
          ([name, value]) => ({
            name,
            value,
            disabled: false
          })
        );
      },
      fundsSearchCategoryFields: {
        [FUND_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]: [
          searchCategoryFunds ||
            UI_OPTIONS[UI_OPTION_KEYS.FUND_SEARCH_CATEGORY]['Investment criteria']
        ]
      },
      setFundsSearchCategoryFieldValue: (field = {}, value) => {
        state.fundsSearchCategoryFields[field.NAME] = value;
      },
      portfoliosSearchCategoryFields: {
        [PORTFOLIO_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]: [
          searchCategoryPortfolios ||
            UI_OPTIONS[UI_OPTION_KEYS.PORTFOLIO_SEARCH_CATEGORY]['Investment criteria']
        ]
      },
      setPortfoliosSearchCategoryFieldValue: (field = {}, value) => {
        state.portfoliosSearchCategoryFields[field.NAME] = value;
      },
      profilerSearchCategoryFields: {
        [PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME]: [
          searchCategoryProfiler ||
            UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics']
        ]
      },
      setProfilerSearchCategoryFieldValue: (field = {}, value) => {
        state.profilerSearchCategoryFields[field.NAME] = value;
      },
      get layout() {
        return utilsStore.windowWidth < 834 ? 'mobile' : 'desktop';
      },
      // Profiler download state [ START ]
      isDownloadProfilerButtonHovered: false,
      setIsDownloadProfilerButtonHovered: (newValue) => {
        state.isDownloadProfilerButtonHovered = newValue;
      },
      toggledProfilerDownloadSearch: false,
      toggleProfilerDownloadSearch: () => {
        state.toggledProfilerDownloadSearch = !state.toggledProfilerDownloadSearch;
      },
      closeProfilerDownloadSearch: () => {
        state.toggledProfilerDownloadSearch = false;
      },
      isDownloadingProfilerSearchOnepagerList: false,
      isSuccessDownloadProfilerSearchOnepagerList: false,
      isDownloadingProfilerSearchContactList: false,
      isSuccessDownloadProfilerSearchContactList: false,
      isDownloadingProfilerSearchProfilesList: false,
      isSuccessDownloadProfilerSearchProfilesList: false,
      onDownloadProfilerSearchClick: (type = '') => {
        const endpoint = {
          onepager: API_ENDPOINTS.GET_PROFILER_PROJECT_ONE_PAGER_LIST_URL,
          contact: API_ENDPOINTS.GET_PROFILER_PROJECT_ONE_CONTACT_LIST_URL,
          profiles: API_ENDPOINTS.GET_PROFILER_PROJECT_PROFILES_LIST_URL
        }[type];
        if (!endpoint) return;

        setProfilerDownloadFlags(state, type, { downloading: true });

        makeRequest({
          endpoint: { ...endpoint, params: { projectId: state.projectId } },
          onSuccess: (response) => {
            // The BE records the downloads before it answers; reconcile the
            // shared snapshot even if this page has unmounted meanwhile.
            refetchSubscription?.();
            if (!state.isRendered) return;

            const projectName =
              profilerProjectStore.allProjects.find((p) => p.id === state.projectId)?.name ||
              state.projectId;

            // Never rejects: a failure is reported through the per-type flags.
            void saveProfilerExportFile({ url: response.url, type, state, projectName });
          },
          onError: (errorMessage, response) => {
            const isLimitExceeded = response?.code === DOWNLOAD_LIMIT_EXCEEDED_CODE;
            if (isLimitExceeded) refetchSubscription?.();
            if (!state.isRendered) return;

            setProfilerDownloadFlags(state, type, { downloading: false, success: false });

            if (isLimitExceeded) {
              reopenForLimitExceededRef.current?.(type, response);
              return;
            }

            const failureLabel = PROFILER_DOWNLOAD_TYPES[type]?.failureLabel;
            if (failureLabel) {
              const detail = errorMessage ? ` ${errorMessage}` : '';
              utilsStore.setHeaderMessage(
                `Failed to download ${failureLabel}.${detail}`,
                HEADER_MESSAGE_TYPES.ERROR
              );
            }
          },
          onFinally: () => {
            if (!state.isRendered) return;
          }
        });
      }
      // Profiler download state [  END  ]
    };
  });

  const {
    bulkExportPendingType,
    bulkExportPrefillData,
    requestBulkExport,
    confirmBulkExport,
    cancelBulkExport,
    reopenForLimitExceeded
  } = useBulkExportConfirm({
    downloads,
    isLoading: isSubscriptionLoading,
    onDownload: (type) => state.onDownloadProfilerSearchClick(type)
  });

  // Contact-list download routing (dialog vs legacy bulk export, incl. the demo
  // rule) lives in the hook — see useProjectContactsDownload. Declared here
  // because it consumes requestBulkExport from the hook above. The download
  // popup closes as the dialog opens, so the mobile sheet cannot cover it.
  const contactsDownload = useProjectContactsDownload({
    requestBulkExport,
    onDialogOpen: state.closeProfilerDownloadSearch
  });
  reopenForLimitExceededRef.current = reopenForLimitExceeded;

  // TD-1720: Mobile breakpoint - below this width, hide button text
  const MOBILE_BREAKPOINT = 834;

  const CustomizeColumnsButton = ({ state, utilsStore }) => {
    // TD-1720: Show icon-only on mobile (< 834px), full text on desktop
    const isMobile = utilsStore.windowWidth < MOBILE_BREAKPOINT;

    return (
      <div className="customize-columns-button-wrap">
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          icon={ICON_BUTTON_ICONS.TABLE}
          innerText={isMobile ? undefined : 'Customize columns'}
          wrapClassName="filter-button customize-columns-button"
          onClick={state.toggleProfilerCustomizeColumns}
          filled={state.isProfilerCustomizeColumnsToggled}
          disabled={!state.results?.length}
        />
      </div>
    );
  };

  CustomizeColumnsButton.propTypes = {
    state: PropTypes.shape({
      toggleProfilerCustomizeColumns: PropTypes.func.isRequired,
      isProfilerCustomizeColumnsToggled: PropTypes.bool.isRequired,
      results: PropTypes.array,
      profilerColumns: PropTypes.array.isRequired,
      searchId: PropTypes.string,
      currentSearch: PropTypes.object,
      isProfilerExtendedFinancialsToggled: PropTypes.bool
    }).isRequired,
    utilsStore: PropTypes.shape({
      windowWidth: PropTypes.number
    }).isRequired
  };

  // TD-2344 Seam 5: single bulk-upload instance for the "Adjust search" re-upload flow. Hosted here
  // (mount once — useBulkCompanySearch's cancelRequest is path-global) and handed to whichever adjust
  // panel is visible. The finalize handoff mirrors the Home ProfilerSearch: onFinalized navigates to
  // the new searchId's results, where the getResults/parse-effect bulk branches (other seams) load it.
  const bulkAdjust = useBulkCompanySearch();
  const [bulkAdjustFile, setBulkAdjustFile] = useState(null);

  // Compared/built against the literal token, not UI_OPTIONS[...].Bulk, since that picker option is
  // env-gated (non-prod only) but the finalize handoff and dropdown-away reset must work everywhere.
  const bulkAdjustCategory = 'companyBulkSearch';

  const onBulkAdjustFinalized = (finalizedSearchId) => {
    if (!finalizedSearchId) {
      return; // §9 error handling: never navigate on a missing searchId
    }
    bulkAdjust.reset();
    setBulkAdjustFile(null);
    if (state.isDesktopFiltersToggled) {
      state.toggleDesktopFilters();
    }
    if (state.isMobileFiltersDisplayed) {
      state.toggleMobileFilters();
    }
    navigate(
      `${paths.SEARCH_RESULTS}?searchCategory=${bulkAdjustCategory}&searchId=${encodeURIComponent(
        finalizedSearchId
      )}&page=1`
    );
  };

  const bulkAdjustProps = {
    bulk: bulkAdjust,
    file: bulkAdjustFile,
    onFileChange: setBulkAdjustFile,
    onFinalized: onBulkAdjustFinalized
  };

  // Leaving the Bulk type in the adjust dropdown drops any staged file / completed review so a later
  // re-entry starts clean. (Inert under the test harness, where mobx `autorun` is a no-op mock.)
  const bulkAdjustRef = useRef(bulkAdjust);
  bulkAdjustRef.current = bulkAdjust;
  useEffect(() => {
    const dispose = autorun(() => {
      const cat =
        state.profilerSearchCategoryFields[PROFILER_SEARCH_CATEGORY_FIELDS.SEARCH_CATEGORY.NAME][0];
      if (cat !== bulkAdjustCategory && bulkAdjustRef.current.status !== 'idle') {
        bulkAdjustRef.current.reset();
        setBulkAdjustFile(null);
      }
    });
    return dispose;
  }, [state]);

  useEffect(() => {
    return () => {
      state.unrender();
      runInAction(() => delete savedState.savedScrollY[state.searchId]);
    };
  }, [state]);

  useEffect(() => {
    return () => {
      clearTimeout(state.profilerPollInterval);
      cancelRequest([
        API_ENDPOINTS.GET_SEARCH_RESULTS,
        API_ENDPOINTS.TRIGGER_PROFILER_SEARCH,
        API_ENDPOINTS.POLL_PROFILER_RESULTS,
        API_ENDPOINTS.GET_PROFILER_COMPANIES_BY_INDUSTRIES
      ]);
    };
  }, [state]);

  useEffect(() => {
    document.title = `transact.digital GmbH - Search results`;
  }, []);

  // V1.9 — Defensive mount-time hydration for Spotlights + column filters.
  //
  // Why: when navigating *back* from a company-detail page, SearchResults
  // remounts with the defaults `activeSpotlights = new Set()` /
  // `columnFilters = {}`. The main hydration runs inside `getResults`
  // (runInAction at line 540-541), which is gated by an URL/state diff
  // check (`compareFields(...)` at line 2503). If that gate ever fails
  // (e.g. cached state, race conditions, future refactors), filters get
  // lost while the results+columns still render. Reading localStorage
  // here, directly off the URL's `searchId`, gives both stores a reliable
  // restore point that doesn't depend on `getResults` firing.
  //
  // Idempotent vs. `getResults`: when both paths fire, the second write
  // is a no-op (same data). When only one fires, the user still sees
  // their chips + column triggers in the restored state.
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchIdFromUrl = urlParams.get('searchId');
    if (!searchIdFromUrl) return;
    runInAction(() => {
      state.activeSpotlights = new Set(getActiveSpotlightsBySearchResultsPage(searchIdFromUrl));
      state.columnFilters = coerceColumnFiltersForLite(
        getColumnFiltersBySearchResultsPage(searchIdFromUrl),
        profilerAccessStore.isProfilerLiteOnly
      );
    });
    // Run only on mount — getResults handles subsequent searchId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (utilsStore.options) {
      runInAction(() => {
        const industryCodesCopy = utilsStore.options[
          OPTION_KEYS.PROFILER_INDUSTRY_CODES
        ].getInitialListsCopy(state.profilerIndustriesListFromURL);
        state.industryCodesFlat = industryCodesCopy.flatList;
        state.industryCodesRaw = industryCodesCopy.rawList;
      });
    }
  }, [utilsStore.options, state, state.profilerIndustriesListFromURL]);

  // INVARIANT: the field holds the minimal COVERING SET, never the expansion — see
  // the matching autorun in ProfilerSearch.jsx. Collapsing here also keeps this bag
  // comparable with `state.currentSearch` (hydrated from the URL, so also collapsed),
  // which `isProfilerIndustryCodesSearchDisabled` relies on to decide whether the
  // Adjust panel's Search button is enabled.
  useEffect(() => {
    const dispose = autorun(() => {
      const selected = collapseIndustryCodes(
        state.industryCodesFlat.filter((code) => code.selected).map((code) => code.value)
      );
      runInAction(() => {
        state.setProfilerIndustryCodesFieldValue(
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.INDUSTRY_CODES,
          selected
        );
      });
    });

    return () => {
      dispose();
    };
  }, [state]);

  useElementHeightCssVar(
    '.page-search-listing .listing-layout .desktop-search-filters-wrapper',
    '--search-results-desktop-filters-height'
  );
  useElementHeightCssVar(
    '.page-search-listing .listing-layout .results-header',
    '--search-results-header-desktop-height'
  );
  useElementHeightCssVar(
    '.page-search-listing .listing-layout .mobile-filters',
    '--search-results-mobile-filters-height'
  );
  useElementHeightCssVar(
    '.page-search-listing .listing-layout .profiler-heatmap-controls-header.mobile-view',
    '--profiler-heatmap-controls-mobile-height'
  );
  // TD-2720 — the heatmap's section header (title + controls + collapse toggle) sits
  // inside the scrolling body, so its height comes off the heatmap's own height.
  useElementHeightCssVar(
    '.page-search-listing .listing-layout .profiler-heatmap-section-header',
    '--profiler-heatmap-section-header-height'
  );
  useElementHeightCssVar(
    '.page-search-listing .listing-layout .profiler-heatmap-section .heatmap-legend',
    '--heatmap-legend-height'
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const targetElement = event.target;
      const searchInputWrapper = document.querySelector('.search-key-input-wrapper-industry-codes');

      if (searchInputWrapper && !searchInputWrapper.contains(targetElement)) {
        if (state.profilerIndustriesSearchTreeToggled) {
          state.toggleProfilerIndustriesSearchTree();
        }
      }
    };

    const onMultiselectInterceptorClick = (payload) => {
      if (payload?.event === 'multiselect-click' && state.profilerIndustriesSearchTreeToggled) {
        state.toggleProfilerIndustriesSearchTree();
      }
    };

    document.addEventListener('click', handleClickOutside, { capture: true });
    pubSub.subscribe(onMultiselectInterceptorClick);

    return () => {
      document.removeEventListener('click', handleClickOutside, { capture: true });
      pubSub.unsubscribe(onMultiselectInterceptorClick);
    };
  }, [state]);

  useEffect(() => {
    const onGlobalClick = (event) => {
      if (!state.toggledProfilerDownloadSearch) {
        return;
      }

      const isClickInside =
        event.target.closest('.download-search-popup') ||
        event.target.closest('#dps-btn-desktop') ||
        // TD-2764: on mobile the download popup lives inside a MobileSheet whose
        // ✕ / backdrop sit outside `.download-search-popup`; exempt the bar/sheets
        // so this capture-phase handler doesn't fight MobileSheet's own dismiss.
        isInsideResultsMobileBar(event.target);
      if (isClickInside) {
        return;
      }

      state.toggleProfilerDownloadSearch();
    };

    document.addEventListener('click', onGlobalClick, { capture: true });

    return () => {
      document.removeEventListener('click', onGlobalClick, { capture: true });
    };
  }, [state]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === 'Enter' &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        const allowedInputsForSearchExecution = [
          // for funds investment criteria
          SEARCH_FIELDS.REVENUE,
          SEARCH_FIELDS.EBITDA,
          SEARCH_FIELDS.EBITDA_MARGIN,
          SEARCH_FIELDS.ENTERPRISE_MIN,
          SEARCH_FIELDS.ENTERPRISE_MAX,
          // for portfolio companies investment criteria
          SEARCH_FIELDS_PORTFOLIO_INVESTMENT_CRITERIA.REVENUE,
          // for profiler company criteria
          PROFILER_SEARCH_FIELDS.INDUSTRY,
          PROFILER_SEARCH_FIELDS.VALUE_CHAIN,
          PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO,
          PROFILER_SEARCH_FIELDS.REVENUE_MIN,
          PROFILER_SEARCH_FIELDS.REVENUE_MAX,
          PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN,
          PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX,
          // for profiler industry codes
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MIN,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.REVENUE_MAX,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MIN,
          PROFILER_INDUSTRY_CODES_SEARCH_FIELDS.EMPLOYEES_MAX
        ].map((searchFieldConfig) => searchFieldConfig.ID);

        if (allowedInputsForSearchExecution.includes(document.activeElement?.id)) {
          // Same (searchType × searchCategory) matrix as onSearch, plus the
          // per-branch `is*SearchDisabled` guard — resolved in one helper.
          if (canExecuteSearchOnEnter(state)) {
            event.preventDefault();
            event.stopPropagation();
            state.onSearch();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [state]);

  useEffect(() => {
    return () => {
      // close all the popups when exiting the page so the lock scroll can be released
      if (state.detailsPopupDisplayed) {
        state.displayDetailsPopup(null);
      }

      if (state.profilerCompanyDetailsDisplayed) {
        state.displayProfilerCompanyDetailsDetails(null);
      }

      if (state.messagePopupDisplayed) {
        state.displayMessagePopup(null);
      }

      if (state.massOutreachPopupDisplayed) {
        state.toggleMassOutreachPopup(null);
      }

      if (state.saveSearchPopupDisplayed) {
        state.toggleSaveProjectPopup(null);
      }

      if (state.saveProfilerSearchPopupDisplayed) {
        state.toggleProfilerSaveProjectPopup(null);
      }

      if (state.isMobileFiltersDisplayed) {
        state.toggleMobileFilters();
      }
    };
  }, [state]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);

    // TD-2344 — Bulk company search opens by searchId only. Its URL carries
    // `searchCategory=companyBulkSearch&searchId=…` with NO `searchType` and
    // NO criteria params, so the searchType/criteria parsing below would throw
    // on the missing `searchType` and wrongly trip `hasInvalidParam`.
    // Short-circuit and load the finalized set straight from the searchId;
    // getResults issues the TRIGGER_PROFILER_SEARCH `{ searchId }` call and
    // populates state. The `loadingSearchId` guard dedupes this effect's
    // re-runs on pagination (dep: location.search) — once loaded,
    // loadingSearchId === searchId so we skip a redundant reload.
    // Compared against the literal token, not UI_OPTIONS[...].Bulk, since that
    // picker option is env-gated (non-prod only) but recognizing a bulk URL
    // must work in every environment.
    const bulkCategory = 'companyBulkSearch';
    if (decodeURIComponent(urlParams.get('searchCategory')) === bulkCategory) {
      const bulkSearchId = urlParams.get('searchId');
      if (!bulkSearchId) {
        runInAction(() => {
          state.resultsError = 'Invalid search params were found in the URL.';
          if (state.isInitialLoading) {
            state.isInitialLoading = false;
          }
        });
        return;
      }
      if (state.loadingSearchId !== bulkSearchId) {
        state.getResults(null, bulkSearchId, bulkCategory);
      }
      return;
    }

    // Parse + validate searchType/searchCategory against the store's currently
    // enabled categories (extracted verbatim to urlParamsHelpers).
    let { searchType, searchCategory, hasInvalidParam } = resolveSearchTypeAndCategory(
      state,
      urlParams
    );

    // The (searchType × searchCategory) parse/hydrate/initial matrices that used
    // to live inline here are now a flat descriptor table in urlParamsHelpers.js
    // (TD-2377 Phase 3). A null descriptor means the combination carries no
    // criteria to parse — identical to the original effect's unmatched inner
    // branches, which left `searchFields` null and fired no search.
    const descriptor = resolveUrlDescriptor(searchType, searchCategory);

    let searchFields = null;
    try {
      if (descriptor) {
        const parsed = parseSearchFieldsFromUrl(descriptor, urlParams);
        searchFields = parsed.searchFields;
        if (parsed.hasInvalidParam) {
          hasInvalidParam = true;
        }
      }
    } catch {
      hasInvalidParam = true;
    }

    if (hasInvalidParam) {
      runInAction(() => {
        state.resultsError = 'Invalid search params were found in the URL.';
        if (state.isInitialLoading) {
          state.isInitialLoading = false;
        }
      });
      return;
    }

    if (descriptor) {
      hydrateSearchFields(state, descriptor, searchFields);
    }

    const queryFields = buildQueryFields(descriptor, searchFields);

    if (compareFields(queryFields, state.currentSearch)) {
      const foundSearchId = urlParams.get('searchId');
      state.getResults(searchFields, foundSearchId, searchCategory);
    }
  }, [state, location.search]);

  useEffect(() => {
    if (state.isLoading || state.isInitialLoading) {
      return;
    }

    const urlParams = new URLSearchParams(location.search);
    const searchCategory = decodeURIComponent(urlParams.get('searchCategory'));
    // A bulk search keeps `currentSearch` empty; see canPaginateResults.
    if (!canPaginateResults(state, searchCategory)) {
      return;
    }

    const fieldsParams = Object.entries(state.currentSearch)
      .map(([k, v]) => `${[k]}=${encodeURIComponent(JSON.stringify(v))}`)
      .join('&');

    // TD-2200 V1 — pagination is list-specific (Heatmap/Chart render all rows
    // on one screen, ignoring pages). When Spotlights / column filters narrow
    // the list, page count needs to shrink so URLs stay valid.
    let pages = Math.ceil(state.filteredAndSortedResults.length / pageSize) || 1;
    const foundPage = urlParams.get('page');
    let page = foundPage ? +foundPage : 1;
    if (isNaN(page) || page < 1) {
      page = 1;
      navigate(
        paths.SEARCH_RESULTS +
          `?searchCategory=${encodeURIComponent(searchCategory)}&page=1&${
            state.searchId ? `searchId=${state.searchId}&` : ''
          }${fieldsParams}`
      );
    } else if (page > pages) {
      page = pages || 1;
      navigate(
        paths.SEARCH_RESULTS +
          `?searchCategory=${encodeURIComponent(searchCategory)}&page=${pages}&${
            state.searchId ? `searchId=${state.searchId}&` : ''
          }${fieldsParams}`
      );
    }
    // TD-2200 V1 — sync state.page / state.pages with the (clamped) page on
    // every pass, including the out-of-range branches above. Otherwise a
    // filter that shrinks the page count leaves state.page stale and the List
    // view renders an empty out-of-range slice until the navigate re-fires.
    if (state.page !== page || state.pages !== pages) {
      runInAction(() => {
        state.page = page || 1;
        state.pages = pages;
      });
    }

    if (state.paginatedSearchId !== state.searchId) {
      runInAction(() => (state.paginatedSearchId = state.searchId));
      navigate(
        paths.SEARCH_RESULTS +
          `?searchCategory=${searchCategory}&page=${page}&${
            state.searchId ? `searchId=${state.searchId}&` : ''
          }${fieldsParams}`
      );
    }
  }, [state, location.search, state.searchId, navigate, state.filteredAndSortedResults.length]);

  useEffect(() => {
    const handleClickOutsideSort = (event) => {
      const isClickInsideSortMenu = event.target.closest('.sort-menu-wrap');
      const isClickInsideSortButtonDesktop = event.target.closest('#sort-btn-desktop');
      const isClickInsideSortButtonMobile = event.target.closest('#sort-btn-mobile');
      // TD-2764: on mobile the Sort control is the bottom action bar's button and
      // its bottom sheet, neither of which matches the old dropdown/button
      // selectors — exempt them or a bar tap would open then instantly re-close.
      const isClickInsideMobileBar = isInsideResultsMobileBar(event.target);

      if (
        isClickInsideSortMenu ||
        isClickInsideSortButtonDesktop ||
        isClickInsideSortButtonMobile ||
        isClickInsideMobileBar
      ) {
        // do nothing
      } else {
        if (state.isSortToggled) {
          state.toggleSort();
        }
      }
    };

    document.addEventListener('click', handleClickOutsideSort);

    return () => {
      document.removeEventListener('click', handleClickOutsideSort);
    };
  }, [state]);

  useEffect(() => {
    const handleClickOutsideSort = (event) => {
      const isClickInsideSortMenu = event.target.closest('.view-menu-wrap');
      const isClickInsideSortButtonDesktop = event.target.closest('#list-change-btn-desktop');
      const isClickInsideSortButtonMobile = event.target.closest('#list-change-btn-mobile');
      // TD-2764: on mobile the View control is the bottom action bar's button and
      // its bottom sheet — exempt them (see the Sort handler above).
      const isClickInsideMobileBar = isInsideResultsMobileBar(event.target);

      if (
        isClickInsideSortMenu ||
        isClickInsideSortButtonDesktop ||
        isClickInsideSortButtonMobile ||
        isClickInsideMobileBar
      ) {
        // do nothing
      } else {
        if (state.isProfilerViewMenuToggled) {
          state.toggleProfilerViewMenu();
        }
      }
    };

    document.addEventListener('click', handleClickOutsideSort);

    return () => {
      document.removeEventListener('click', handleClickOutsideSort);
    };
  }, [state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideColumnsPopup = event.target.closest('.customize-columns-main-wrap');
      const isCustomizeColumnsButton = event.target.closest('.customize-columns-button');
      // TD-2764: on mobile Customize is opened from the bottom action bar's "More"
      // sheet and rendered inside a bottom sheet — exempt the bar and both sheets.
      const isInsideMobileBar = isInsideResultsMobileBar(event.target);

      if (!isInsideColumnsPopup && !isCustomizeColumnsButton && !isInsideMobileBar) {
        if (state.isProfilerCustomizeColumnsToggled) {
          state.toggleProfilerCustomizeColumns();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [state]);

  useEffect(() => {
    const handleClickOutsideSort = (event) => {
      const isClickInsideMenu = event.target.closest('.heatmap-control-menu-wrap.tile-size');
      const isClickInsideSortButton = event.target.closest('#profiler-tile-size-metric');

      if (isClickInsideMenu || isClickInsideSortButton) {
        // do nothing
      } else {
        if (state.isProfilerHeatmapTileSizeMenuToggled) {
          state.toggleProfilerHeatmapTileSizeMenu();
        }
      }
    };

    document.addEventListener('click', handleClickOutsideSort);

    return () => {
      document.removeEventListener('click', handleClickOutsideSort);
    };
  }, [state]);

  useEffect(() => {
    const handleClickOutsideSort = (event) => {
      const isClickInsideMenu = event.target.closest('.heatmap-control-menu-wrap.color-coding');
      const isClickInsideSortButton = event.target.closest('#profiler-color-coding-metric');

      if (isClickInsideMenu || isClickInsideSortButton) {
        // do nothing
      } else {
        if (state.isProfilerHeatmapColorCodingMenuToggled) {
          state.toggleProfilerHeatmapColorCodingMenu();
        }
      }
    };

    document.addEventListener('click', handleClickOutsideSort);

    return () => {
      document.removeEventListener('click', handleClickOutsideSort);
    };
  }, [state]);

  // TD-2461 — close the Bulk-exclude dropdown on an outside click.
  useEffect(() => {
    const handleClickOutsideBulkExclude = (event) => {
      const isInsideMenu = event.target.closest('.bulk-exclude-menu-wrap');
      const isTrigger = event.target.closest('#bulk-exclude-trigger');
      if (!isInsideMenu && !isTrigger && state.isBulkExcludeMenuToggled) {
        state.toggleBulkExcludeMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutsideBulkExclude);

    return () => {
      document.removeEventListener('click', handleClickOutsideBulkExclude);
    };
  }, [state]);

  useEffect(() => {
    if (
      (utilsStore.windowWidth < 600 || profilerAccessStore.isProfilerLiteOnly) &&
      state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
      state.profilerView === PROFILER_VIEWS.CHART
    ) {
      state.setProfilerView(DEFAULT_PROFILER_VIEW);
    }
  }, [state, utilsStore.windowWidth, profilerAccessStore.isProfilerLiteOnly]);

  /**
   * RESPONSIVE COLUMN RESTORATION LOGIC
   * ==================================
   * This useEffect handles column restoration when resizing from small to large screen.
   *
   * Problem: When windowWidth < 1366px, the system uses priority column logic that limits
   * column selections. When resizing back to >= 1366px, state.profilerColumns retains
   * the limited selections, causing columns to remain hidden.
   *
   * Solution: Detect transitions from < 1366px to >= 1366px and restore appropriate
   * desktop column selections while preserving any custom user arrangements.
   */
  useEffect(() => {
    const isDesktopMode = utilsStore.windowWidth >= 1366;
    const isProfilerSearch =
      state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler;

    // Skip restoration when Financials-only mode is active: that mode is also
    // a low-column-count state, and reseeding columns here would silently
    // undo the user's toggle (TD-2071).
    if (
      isDesktopMode &&
      isProfilerSearch &&
      !state.isProfilerExtendedFinancialsToggled &&
      state.profilerColumns?.length
    ) {
      // Check if current columns appear to be in "limited" mode (< 8 selected columns typically indicates small screen mode)
      const selectedColumns = state.profilerColumns.filter((col) => col.selected && !col.hidden);
      const seemsLikeSmallScreenState = selectedColumns.length < 8;

      if (seemsLikeSmallScreenState) {
        runInAction(() => {
          // Restore appropriate desktop column selections based on search criteria
          const restoredColumns = getInitialProfilerColumns(
            state.currentSearch,
            utilsStore.windowWidth,
            profilerAccessStore.isProfilerLiteOnly
          );
          state.profilerColumns = restoredColumns;

          // Save the restored state
          if (state.searchId) {
            saveProfilerColumnsBySearchResultsPage(state.profilerColumns, state.searchId);
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    utilsStore.windowWidth,
    state.searchType,
    state.isProfilerExtendedFinancialsToggled,
    state.searchId
  ]);

  const profilerCompanyCriteriaFields = getProfilerCompanyCriteriaSearchFields(state);
  const profilerIndustryCodesFields = getProfilerIndustryCodesSearchFields(state);
  const fundsInvestmentCriteriaFields = getFundsInvestmentCriteriaSearchFields(
    state,
    state.uiFormStateFundsInvestmentCriteria,
    profilerAccessStore.canUseProfilerSearch,
    authStore.userAccountType === ACCOUNT_TYPES.AGENT
  );
  const portfoliosInvestmentCriteriaFields = getPortfoliosInvestmentCriteriaSearchFields(state);

  // TD-2377 Phase 3 Part B — the view-flag derivation (loading/result/download/
  // save/outreach gating + paginated-view booleans) lives in deriveSearchResultsFlags.
  // Its plain-JS `&&`/`||`/ternary chains counted against this component's own
  // cognitive complexity; the pure helper is unit-tested against a plain state.
  const {
    isLoading,
    hasResults,
    hasError,
    showDetailsPopup,
    showMessagePopup,
    showMassOutreachPopup,
    showSaveSearchPopup,
    showProfilerSaveSearchPopup,
    showNoResults,
    showResults,
    showPaging,
    canDownload,
    disableSaveSearch,
    disableProfilerSaveSearch,
    canSaveSearch,
    fillSaveSearchIcon,
    disableMassOutreach,
    excludedResults,
    currentSearchType,
    profilerColumns
  } = deriveSearchResultsFlags(state, { projectStore, profilerProjectStore, outreachStore });

  // URL-based navigation to company details
  // Uses company ID in URL path with ref param for back button label
  // For name search: includes searchKey for GET_PROFILER_COMPANY_FROM_SEARCH endpoint
  // For characteristics/industry search: includes searchId for GET_PROFILER_COMPANY_SUPPLEMENTARY_DATA
  const navigateToCompanyDetails = (company) => {
    if (!company) return;
    savedState.saveScrollY(state.searchId);
    const path = utilsStore.getCompanyDetailsPath(company.id, {
      ref: 'search results',
      searchKey: state.profilerNameSearchSearchKey || undefined,
      // Pass searchId for non-name searches (characteristics/industry) to fetch supplementary data
      searchId: !state.profilerNameSearchSearchKey && state.searchId ? state.searchId : undefined
    });
    navigate(path);
  };

  // V2.1 — single-call helper for the result-count line shared between the
  // mobile (`.total-number`) and desktop (`.total-num`) render branches.
  // Previously written as a JSX nested ternary; Sonar flagged both sites
  // for readability. Pull the branching into a guard-clause function so
  // each render slot is a clean `{renderResultCount()}` call.
  // `profilerOnly = true` adds the `currentSearchType === Profiler` gate
  // around the filtered-count format (the desktop branch is already inside
  // a Profiler-only render so it doesn't need the extra check).
  const renderResultCount = ({ profilerOnly } = {}) => {
    if (state.isInitialLoading || state.isLoading) return <>&#8202;</>;
    const total = state.results.length.toLocaleString('en-US');
    const filtersActive = hasAnyActiveFilter(state.activeSpotlights, state.columnFilters);
    const isProfiler = currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler;
    if (filtersActive && (!profilerOnly || isProfiler)) {
      // Y = non-excluded total (`sortedResults`), so the "X out of Y" ratio
      // compares like with like — X (`filteredAndSortedResults`) already
      // drops excluded companies, whereas `total` counts them.
      const visibleTotal = state.sortedResults.length.toLocaleString('en-US');
      return (
        <>
          {state.filteredAndSortedResults.length.toLocaleString('en-US')} out of {visibleTotal}{' '}
          results
        </>
      );
    }
    return `${total} results`;
  };

  // const profilerColumns =
  //   utilsStore.windowWidth >= 1366
  //     ? state.profilerColumns
  //     : getInitialProfilerColumns(state.currentSearch, utilsStore.windowWidth);

  // V2.4 — extract the 5-branch nested ternary that built the download-
  // button tooltip content. Same shape was inlined 4× across this file
  // (onepager + contact-list, mobile + desktop toolbar) — Sonar flagged
  // every nested branch. Single helper with short-circuit returns; reads
  // `state.isLoading || state.isInitialLoading` and `state.sortedResults.length`
  // matching the SearchResults page (vs `ProfilerProjectView.jsx`'s helper
  // which uses `state.nonExcludedResultsCount`).
  // TD-2648: the download trigger no longer carries the `profilerexportdisabled`
  // right, so each entry inside the popup does.
  const profilerExportGate = (hasRoleAccess, extraDisabled = false) =>
    exportEntryGate({
      isExportDisabled: profilerAccessStore.isProfilerExportDisabled,
      hasRoleAccess,
      extraDisabled
    });

  // A used-up allowance shows no tooltip: the popup's counter already says
  // "Download Limit Reached", and excluding companies would not help.
  const renderDownloadTooltip = ({
    hasAccess,
    limit,
    isAllowanceReached,
    isOverCap,
    isDownloading,
    isSuccess,
    noAccessNode
  }) => {
    if (!hasAccess) return noAccessNode;
    if (state.isLoading || state.isInitialLoading || isSubscriptionLoading) {
      return <>Loading results, please wait.</>;
    }
    if (isAllowanceReached) return null;
    if (isOverCap) return listCapTooltip(limit);
    if (isDownloading) return <>Downloading. Please wait.</>;
    if (isSuccess) return <>Downloaded successfully</>;
    return null;
  };

  const profilerColumnHeaderJSX = {
    [PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_NAME]: (
      <div
        className="col name"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_NAME
          ).minWidth
        }}
      >
        <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Company name</span>
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.LOCATION]: (
      <div
        className="col headquarter"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.LOCATION
          ).minWidth
        }}
      >
        <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Location</span>
        <ColumnFilterTrigger columnKey="headquarters" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY]: (
      <div
        className={`col industry ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.INDUSTRY
          ).minWidth
        }}
      >
        Industry
        <ColumnFilterTrigger columnKey="industries" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.VALUE_CHAIN]: (
      <div
        className={`col value-chain ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.VALUE_CHAIN
          ).minWidth
        }}
      >
        Value chain
        <ColumnFilterTrigger columnKey="valueChain" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCTS]: (
      <div
        className={`col products ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCTS
          ).minWidth
        }}
      >
        Products
        <ColumnFilterTrigger columnKey="products" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.SALES]: (
      <div
        className="col revenue"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.SALES
          ).minWidth
        }}
      >
        <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Sales (€M)</span>
        <ColumnFilterTrigger columnKey="revenue" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.EMPLOYEES]: (
      <div
        className="col employees"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.EMPLOYEES
          ).minWidth
        }}
      >
        <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>Employees</span>
        <ColumnFilterTrigger columnKey="employees" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.CAGR3Y]: (
      <div
        className="col cagr"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.CAGR3Y
          )?.minWidth
        }}
      >
        <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>CAGR 3Y (%)</span>
        <ColumnFilterTrigger columnKey="cagr3Y" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.CAGR4Y]: (
      <div
        className="col cagr"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.CAGR4Y
          )?.minWidth
        }}
      >
        <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>CAGR 4Y (%)</span>
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.EBIT_MARGIN]: (
      <div
        className="col ebit-margin"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.EBIT_MARGIN
          )?.minWidth
        }}
      >
        <span className={dataSourceClass(DATA_SOURCE.REGISTRY)}>EBIT Margin (%)</span>
        <ColumnFilterTrigger columnKey="ebitPercentage" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.SITUATION]: (
      <div
        className="col situation"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.SITUATION
          )?.minWidth
        }}
      >
        <span>Situation</span>
        <ColumnFilterTrigger columnKey="situation" state={state} />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.SHAREHOLDERS]: (
      <div
        className="col shareholders"
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.SHAREHOLDERS
          )?.minWidth
        }}
      >
        <span>Shareholders</span>
        <OwnershipBasisToggle
          label="Show shareholders as"
          value={state.ownershipDisplayBasis}
          searchedBasis={
            state.currentSearch?.[PROFILER_SEARCH_FIELDS.OWNERSHIP_STRUCTURES.NAME]?.length
              ? state.ownershipSearchedBasis
              : null
          }
          onChange={state.setOwnershipDisplayBasis}
        />
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_DESCRIPTION]: (
      <div
        className={`col company-description ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.COMPANY_DESCRIPTION
          )?.minWidth
        }}
      >
        Company description
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION]: (
      <div
        className={`col product-portfolio-description ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.PRODUCT_PORTFOLIO_DESCRIPTION
          )?.minWidth
        }}
      >
        Product portfolio
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.KEY_PRODUCTS]: (
      <div
        className={`col key-products ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.KEY_PRODUCTS
          )?.minWidth
        }}
      >
        Key products
      </div>
    ),
    [PROFILER_SEARCH_RESULTS_COLUMNS.KEY_END_MARKETS]: (
      <div
        className={`col key-end-markets ${dataSourceClass(DATA_SOURCE.WEBSITE)}`}
        style={{
          minWidth: profilerColumns.find(
            (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.KEY_END_MARKETS
          )?.minWidth
        }}
      >
        Key end-markets
      </div>
    )
    // THE BELOW IS COMMENTED OUT AS IT'S TO BE IMPLEMENTED ON BACKEND, ONCE IMPLEMENTED THERE, JUST UNCOMMENT
    // [PROFILER_SEARCH_RESULTS_COLUMNS.TOP_MANAGEMENT]: (
    //   <div
    //     className="col top-management"
    //     style={{
    //       minWidth: profilerColumns.find(
    //         (column) => column.id === PROFILER_SEARCH_RESULTS_COLUMNS.TOP_MANAGEMENT
    //       )?.minWidth
    //     }}>
    //     Top management
    //   </div>
    // )
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const profilerSearchLoader = useSearchResultsLoader({
    keyRender: Object.entries(state.currentSearch)
      .map(([key, val]) => key + String(val))
      .join(''),
    withRationale: true,
    criteria: [
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME]
        ? [
            {
              name: 'Industry',
              display: state.currentSearch[PROFILER_SEARCH_FIELDS.INDUSTRY.NAME]
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.NAME]
        ? [
            {
              name: 'Value chain',
              display: state.currentSearch[PROFILER_SEARCH_FIELDS.VALUE_CHAIN.NAME]
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.NAME]
        ? [
            {
              name: 'Product portfolio',
              display: state.currentSearch[PROFILER_SEARCH_FIELDS.PRODUCT_PORTFOLIO.NAME]
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.REVENUE_MIN.NAME] ||
      state.currentSearch[PROFILER_SEARCH_FIELDS.REVENUE_MAX.NAME]
        ? [
            {
              name: 'Sales (€M)',
              display: formatNumberRange(
                state.currentSearch[PROFILER_SEARCH_FIELDS.REVENUE_MIN.NAME],
                state.currentSearch[PROFILER_SEARCH_FIELDS.REVENUE_MAX.NAME],
                PUNCTUATION.EN_DASH,
                true
              )
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME] ||
      state.currentSearch[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME]
        ? [
            {
              name: 'CAGR (%)',
              display: formatNumberRange(
                state.currentSearch[PROFILER_SEARCH_FIELDS.CAGR_MIN.NAME],
                state.currentSearch[PROFILER_SEARCH_FIELDS.CAGR_MAX.NAME],
                PUNCTUATION.EN_DASH,
                true
              )
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.CAGR_PERIOD.NAME]?.length
        ? [
            {
              name: 'CAGR Period',
              display: utilsStore.getOptionName(
                state.currentSearch,
                PROFILER_SEARCH_FIELDS.CAGR_PERIOD
              )[0]
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME] ||
      state.currentSearch[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME]
        ? [
            {
              name: 'EBIT Margin (%)',
              display: formatNumberRange(
                state.currentSearch[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MIN.NAME],
                state.currentSearch[PROFILER_SEARCH_FIELDS.EBIT_MARGIN_MAX.NAME],
                PUNCTUATION.EN_DASH,
                true
              )
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME] ||
      state.currentSearch[PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.NAME]
        ? [
            {
              name: 'Employees',
              display: formatNumberRange(
                state.currentSearch[PROFILER_SEARCH_FIELDS.EMPLOYEES_MIN.NAME],
                state.currentSearch[PROFILER_SEARCH_FIELDS.EMPLOYEES_MAX.NAME],
                PUNCTUATION.EN_DASH,
                true
              )
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.HEADQUARTER.NAME]?.length
        ? [
            {
              name: 'Country',
              display: utilsStore
                .getOptionName(state.currentSearch, PROFILER_SEARCH_FIELDS.HEADQUARTER)
                .join(', ')
            }
          ]
        : []),
      ...(() => {
        const cv = (state.currentSearch[PROFILER_SEARCH_FIELDS.CITY_ZIPCODE.NAME] || '').trim();
        return cv
          ? [
              {
                name: 'City/Zipcode',
                display: cv
              }
            ]
          : [];
      })(),
      ...(() => {
        const dv = state.currentSearch[PROFILER_SEARCH_FIELDS.DISTANCE.NAME];
        const hasValue = dv !== undefined && dv !== null && `${dv}`.length > 0;
        return hasValue
          ? [
              {
                name: 'Distance (km)',
                display: `${dv} km`
              }
            ]
          : [];
      })(),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.SITUATION.NAME]?.length
        ? [
            {
              name: 'Situation',
              display: utilsStore
                .getOptionName(state.currentSearch, PROFILER_SEARCH_FIELDS.SITUATION)
                .join(', ')
            }
          ]
        : []),
      ...(state.currentSearch[PROFILER_SEARCH_FIELDS.LISTING_STATUS.NAME]?.length
        ? [
            {
              name: PROFILER_SEARCH_FIELDS.LISTING_STATUS.LABEL,
              display: utilsStore
                .getOptionName(state.currentSearch, PROFILER_SEARCH_FIELDS.LISTING_STATUS)
                .join(', ')
            }
          ]
        : []),
      ...[PROFILER_SEARCH_FIELDS.OWNERSHIP_STRUCTURES, PROFILER_SEARCH_FIELDS.OWNERSHIP_BASIS]
        .filter((field) => state.currentSearch[field.NAME]?.length)
        .map((field) => ({
          name: field.LABEL,
          display: utilsStore.getOptionName(state.currentSearch, field).join(', ')
        }))
    ],
    start:
      isLoading &&
      currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
      state.searchCategory ===
        UI_OPTIONS[UI_OPTION_KEYS.PROFILER_SEARCH_CATEGORY]['Characteristics'] &&
      !state.loadingSearchId
  });

  if (state.profilerNameSearchSelectedCompany) {
    return (
      <StaticPageLayout page="projects-listing">
        {/* TD-2392 — this inline name-search CompanyDetails is an early return
            that bypasses the main-return legend below; the CompanyDetails
            component doesn't mount its own, so mount it here or the profile's
            "Show data sources" header toggle is inert. (Name search is always a
            Profiler search, so no search-type gate is needed.) */}
        <DataSourceFloatingLegend />
        <CompanyDetails
          key={state.profilerNameSearchSelectedCompany.id}
          company={state.profilerNameSearchSelectedCompany}
          displayDetails={() => {
            runInAction(() => {
              state.profilerNameSearchSelectedCompany = null;
            });
          }}
          project={{ ownerId: authStore.currentUserId }}
          // style={style}
        />
      </StaticPageLayout>
    );
  }

  // Company details now opens via URL navigation (navigateToCompanyDetails)
  // instead of inline modal rendering

  const renderMobileToolbar = () => (
    <div className="mobile-filters">
      <div className="total-number">{renderResultCount({ profilerOnly: true })}</div>
      <div
        className={`filters-download-container${
          isUnifiedFiltersEnabled &&
          currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler
            ? ' filters-download-container--unified'
            : ''
        }`}
      >
        {/* TD-1717 Adjust-search (funnel). TD-2764: Profiler mobile moves this
            into the bottom ResultsMobileActionBar; other search types keep it
            here in the top toolbar. */}
        {currentSearchType !== UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler && (
          <IconButton
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.FILTER}
            innerText="Adjust search"
            wrapClassName="mobile-adjust-search-btn"
            onClick={state.toggleMobileFilters}
          />
        )}
        {/* Unified-filter model — mobile [+ Add filter] + drawer, beside the
            mobile "Adjust search". The panel is a fixed drawer on mobile.
            AddFilterAnchor is its own observer so opening does not re-render
            the whole SearchResults tree (TD-2626 open lag). */}
        {isUnifiedFiltersEnabled &&
          currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
          isProfilerListVisible(state.profilerView) && (
            <AddFilterAnchor store={state} variant="mobile" />
          )}
        {/* TD-2764: Profiler mobile results actions moved to the bottom
            ResultsMobileActionBar (rendered below, after the results body). */}
        {currentSearchType !== UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
          renderStandardToolbarActions()}
      </div>
      {/* V2.6 — Spotlights bar on mobile. The desktop render at the
                  `.results-header` block below (~line 4497) was the only V1
                  site for this bar; the mobile branch went without, so
                  Industries / Characteristics searches on phone widths
                  showed no Spotlights at all. Same gates as desktop
                  (Profiler search type, List view, post-loading) and the
                  same shared exclusion rule as desktop
                  (`categoryExcludedSpotlightIds`), so Industries and Bulk
                  hide High matches here too. The `at-mobile`
                  modifier ties this site to the V2.6 horizontal-scroll
                  `@media (max-width: 1200px)` rule in `.spotlights-bar`. */}
      {!isUnifiedFiltersEnabled &&
        currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
        isProfilerListVisible(state.profilerView) &&
        !state.isInitialLoading &&
        !state.isLoading && (
          <div className="results-header-top-row at-mobile">
            <SpotlightsBar
              activeSpotlights={state.activeSpotlights}
              spotlightCounts={state.spotlightCounts}
              onToggleSpotlight={state.toggleSpotlight}
              excludedSpotlightIds={state.categoryExcludedSpotlightIds}
            />
          </div>
        )}
    </div>
  );

  const renderDesktopSearchFilters = () => (
    <div className="desktop-search-filters-wrapper desktop-only">
      {/* TD-1723: Adjust search visible in ALL Profiler views (list, heatmap, chart) */}
      {currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler && (
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          icon={ICON_BUTTON_ICONS.FILTER}
          innerText="Adjust search"
          wrapClassName="filter-button"
          onClick={state.toggleDesktopFilters}
          filled={state.isDesktopFiltersToggled}
        />
      )}
      {/* Unified-filter model — the [+ Add filter] entry point sits beside
          "Adjust search" (client-side narrow vs server-side re-run). List view
          only, behind the VITE_UNIFIED_FILTERS flag. AddFilterAnchor isolates
          isAddFilterPanelOpen so opening stays cheap (TD-2626). */}
      {isUnifiedFiltersEnabled &&
        currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
        isProfilerListVisible(state.profilerView) && (
          <AddFilterAnchor store={state} variant="desktop" disabled={!state.results?.length} />
        )}
      {/* Customize columns applies wherever the list is on screen — List view and,
          since TD-2720, the stacked Heatmap view. Chart view has no columns. */}
      {currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
        isProfilerListVisible(state.profilerView) && (
          <>
            <IconButton
              type={ICON_BUTTON_TYPES.BLUE}
              icon={ICON_BUTTON_ICONS.TABLE}
              innerText="Customize columns"
              wrapClassName="filter-button"
              onClick={state.toggleProfilerCustomizeColumns}
              filled={state.isProfilerCustomizeColumnsToggled}
              disabled={!state.results?.length}
            />
            {state.isProfilerCustomizeColumnsToggled && renderCustomizeColumnsMenu()}
          </>
        )}
      {renderDesktopListViewButton()}
      {renderDesktopViewToggleButton()}
      {state.isProfilerViewMenuToggled && renderDesktopViewMenu()}
      {renderDesktopSortButtonDesktop()}
      {state.isSortToggled && renderDesktopSortMenu()}
      <DataSourceToggleIcon
        id="data-sources-btn-search-desktop"
        wrapClassName="filter-button-no-text"
      />
      {renderDesktopDownloadButtonDesktop()}
      {state.toggledProfilerDownloadSearch && renderDesktopDownloadPopup()}

      <DesktopFilters
        mainState={state}
        bulkAdjust={bulkAdjustProps}
        fundsInvestmentCriteriaFields={fundsInvestmentCriteriaFields}
        portfoliosInvestmentCriteriaFields={portfoliosInvestmentCriteriaFields}
        profilerCompanyCriteriaFields={profilerCompanyCriteriaFields}
        profilerIndustryCodesFields={profilerIndustryCodesFields}
      />
    </div>
  );

  // TD-2456 — visibility gate for the pill-filter rail: List view, results
  // loaded. The shared <PillFilterRail> handles the "at least one pill active"
  // check + rendering. Two instances are mounted: the `desktop` variant inside
  // the sticky `.results-header` (hidden on mobile via CSS) and the `mobile`
  // variant outside the header (so active pill filters stay visible on mobile).
  const isPillRailVisible =
    isProfilerListVisible(state.profilerView) && !state.isInitialLoading && !state.isLoading;

  // TD-2720 — is <ProfilerColumnHeaderRow/> on screen? It carries the blue rule that
  // closes the table, so this header hides its own (colour only — see the note in
  // search-results.less). Mirrors that row's render conditions in
  // <SearchResultsBody/>, `showResults` included, so a loading or empty state keeps the
  // rule on the header.
  const hasDetachedColumnHeader =
    showResults &&
    state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
    isProfilerListVisible(state.profilerView);

  // TD-2720 — this header now carries only the result count and the active filters.
  // The column-header row moved into <ProfilerColumnHeaderRow/> and the heatmap's
  // metric controls into <ProfilerHeatmapSection/>, both rendered by
  // <SearchResultsBody/> so the heatmap can stack above the list.
  const renderProfilerResultsHeader = () => (
    <div className={`results-header${hasDetachedColumnHeader ? ' with-detached-cols' : ''}`}>
      {/* TD-2200 V1.3 — SpotlightsBar moved here from the toolbar to share a
                    row with the result count (left-aligned with the result table's
                    "Company name" column; result count right-aligned). The toolbar
                    row no longer carries Spotlights, so the Full-HD overlap is gone. */}
      <div className="results-header-top-row">
        {/* Unified-filter model folds the presets into the Add-filter panel, so
            the standalone spotlights bar is hidden when the flag is on. */}
        {!isUnifiedFiltersEnabled &&
          isProfilerListVisible(state.profilerView) &&
          !state.isInitialLoading &&
          !state.isLoading && (
            <SpotlightsBar
              activeSpotlights={state.activeSpotlights}
              spotlightCounts={state.spotlightCounts}
              onToggleSpotlight={state.toggleSpotlight}
              // V1.4 — hide "High matches" on Industry searches.
              // Industry searches return rows without an AI-rationale
              // `scoreLabel`, so the spotlight would always read 0.
              excludedSpotlightIds={state.categoryExcludedSpotlightIds}
            />
          )}
        <div className="results-header-right">
          <div className="total-num">{renderResultCount()}</div>
          {/* Flag-off keeps the control in the header (beside the count). Flag-on
              relocates it into the UnifiedChipRail, with the filters it acts on. */}
          {!isUnifiedFiltersEnabled &&
            isProfilerListVisible(state.profilerView) &&
            !state.isInitialLoading &&
            !state.isLoading &&
            state.hasEffectiveFilter && <BulkExcludeControl store={state} />}
        </div>
      </div>
      {/* TD-2456 — pill filters surface as labelled, removable chips (desktop
          variant, inside the sticky header). A separate mobile variant is
          rendered outside `.results-header` at the call site, because the header
          is hidden on mobile. */}
      {isUnifiedFiltersEnabled ? (
        <UnifiedChipRail store={state} variant="desktop" visible={isPillRailVisible} />
      ) : (
        <PillFilterRail store={state} variant="desktop" visible={isPillRailVisible} />
      )}
    </div>
  );

  const renderStandardResultsHeader = () => (
    <div className="results-header">
      <div className="total-num">
        {!state.isInitialLoading && !state.isLoading ? (
          `${state.results.length} results`
        ) : (
          <>&#8202;</>
        )}
      </div>
      <div className="cols funds-search-cols">
        <div className="col name">{NAME_COLUMN_LABELS[currentSearchType] || 'Entry name'}</div>
        <div className="col industry-focus">Industry focus</div>
        <div className="col geo-focus">Geo focus</div>
        <div className="col enterprise-value">EV (€M)</div>
        <div className="col equity-value">EQV (€M)</div>
        <div className="col actions funds-search-actions">
          <div className="buttons">
            {renderStandardHeaderSaveButton()}
            {renderStandardHeaderOutreachButton()}
            {renderStandardHeaderDownloadButton()}
            {renderStandardHeaderSortButton()}
            {state.isSortToggled && renderStandardHeaderSortMenu()}
          </div>
        </div>
      </div>
    </div>
  );

  // TD-2764 (Phase 2, Option B) — the mobile Profiler Search-results bottom
  // action bar. A portaled `ResultsMobileActionBar` (browser top layer, like
  // Projects/Team) carrying the results actions:
  //   Adjust · View · Sort  inline  ·  [Save] primary  ·  More{ Customize,
  //   Download, data sources }.
  // View / Sort / Customize / Download open as native-<dialog> `MobileSheet`s
  // (top layer), gated on the mobile layout so their showModal + scroll-lock
  // never fire on the CSS-hidden desktop toolbar, which shares these toggle
  // flags. Add-filter is deliberately NOT moved here — it stays in the top row
  // with the pill rail (its own perf-isolated drawer, TD-2626/TD-2756).
  const renderSearchMobileActionBar = () => {
    const currentView = PROFILER_VIEWS_LIST.find((view) => view.value === state.profilerView);
    const isMobile = state.layout === 'mobile';
    const barActions = [
      {
        key: 'adjust',
        label: 'Adjust',
        icon: ICON_BUTTON_ICONS.FILTER,
        onClick: state.toggleMobileFilters
      },
      {
        key: 'view',
        label: 'View',
        icon: currentView?.icon,
        onClick: state.toggleProfilerViewMenu,
        active: state.isProfilerViewMenuToggled,
        disabled: isLoading || !hasResults
      },
      {
        key: 'sort',
        label: 'Sort',
        icon:
          state.effectiveSort !== 'default'
            ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
            : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED,
        onClick: state.toggleSort,
        active: state.isSortToggled || state.effectiveSort !== 'default',
        disabled: isLoading || !hasResults
      }
    ];
    const barPrimary = {
      // Mirror the desktop Save button's saved-state indicator (TD-2764 review):
      // once the search is a project, fillSaveSearchIcon is true → show "Saved"
      // in the done/selected style. The desktop tooltip's tap-through link to the
      // project is desktop-only; on mobile the project is reached via Projects.
      label: fillSaveSearchIcon ? 'Saved' : 'Save',
      icon: ICON_BUTTON_ICONS.FLOPPY_2_FILL,
      active: fillSaveSearchIcon,
      onClick: () => {
        if (canSaveSearch && !state.isProfilerSavingSearch) {
          state.toggleProfilerSaveProjectPopup(true);
        }
      },
      disabled: disableProfilerSaveSearch || state.isProfilerSavingSearch
    };
    // Download lives in the More sheet here, so More carries the in-progress
    // state while an export runs (Projects/Team show it on Download).
    const isExportInFlight = isAnyProfilerDownloadInFlight(state);
    const barMoreActions = [
      {
        key: 'customize',
        label: 'Customize columns',
        icon: ICON_BUTTON_ICONS.TABLE,
        onClick: state.toggleProfilerCustomizeColumns,
        disabled: !state.results?.length
      },
      {
        key: 'download',
        label: 'Download',
        icon: downloadStateIcon({ isDownloading: isExportInFlight }) ?? ICON_BUTTON_ICONS.DOWNLOAD,
        onClick: state.toggleProfilerDownloadSearch,
        disabled: isProfilerSearchDownloadDisabled || !canDownload || !state.projectId
      },
      {
        key: 'data-sources',
        label: utilsStore.showDataSources ? 'Hide data sources' : 'Show data sources',
        icon: ICON_BUTTON_ICONS.INFO_CIRCLE,
        onClick: () => utilsStore.setShowDataSources(!utilsStore.showDataSources)
      }
    ];
    return (
      <>
        <ResultsMobileActionBar
          actions={barActions}
          primary={barPrimary}
          moreActions={barMoreActions}
          moreBusy={isExportInFlight}
          moreLabel="More"
          sheetTitle="More actions"
          ariaLabel="Search results actions"
          breakpoint="to-833"
        />
        {/* Native-<dialog> bottom sheets for the menus the bar opens. Gated on
            the mobile layout so showModal never fires on the desktop toolbar. */}
        <MobileSheet
          isOpen={isMobile && state.isProfilerViewMenuToggled}
          onClose={state.toggleProfilerViewMenu}
          title="View"
        >
          {renderProfilerViewOptions()}
        </MobileSheet>
        <MobileSheet
          isOpen={isMobile && state.isSortToggled}
          onClose={state.toggleSort}
          title="Sort by"
        >
          {renderProfilerSortOptions()}
        </MobileSheet>
        <MobileSheet
          isOpen={isMobile && state.isProfilerCustomizeColumnsToggled}
          onClose={state.toggleProfilerCustomizeColumns}
          title="Customize columns"
        >
          {renderCustomizeColumnsMenu({ isMobile: true })}
        </MobileSheet>
        <MobileSheet
          isOpen={isMobile && state.toggledProfilerDownloadSearch}
          onClose={state.toggleProfilerDownloadSearch}
          title="Download"
        >
          {renderProfilerDownloadPopup()}
        </MobileSheet>
      </>
    );
  };

  const renderStandardToolbarActions = () => (
    <div className="actions">
      {renderStandardSaveSearchButton()}
      {renderStandardOutreachButton()}
      {renderStandardDownloadButton()}
      {renderStandardSortButton()}
      {state.isSortToggled && (
        <div className="sort-menu-wrap">
          {state.agentSortOptions.map((option) => {
            return (
              <IconButton
                key={option.value}
                hoverType={ICON_BUTTON_TYPES.BLUE}
                withBorder={false}
                innerText={option.shortName || option.name}
                onClick={() => state.selectSortOption(option.value) & state.toggleSort()}
                disabled={state.selectedSort === option.value}
                filled={state.selectedSort === option.value}
                {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
              />
            );
          })}
        </div>
      )}
    </div>
  );

  // TD-2530: the desktop dropdown and the mobile bottom sheet rendered this menu
  // from two copies that had drifted apart only in a wrapper class and one
  // stopPropagation. One renderer now backs both; `isMobile` carries the two real
  // differences.
  const handleColumnReorder = (event) => {
    const { active, over } = event;
    if (active.id === over.id) return;

    runInAction(() => {
      state.profilerColumns = reorderProfilerColumns(state.profilerColumns, active.id, over.id);
      saveProfilerColumnsBySearchResultsPage(state.profilerColumns, state.searchId);
    });
  };

  const handleToggleColumnSelected = (item) => {
    runInAction(() => {
      item.selected = !item.selected;
      saveProfilerColumnsBySearchResultsPage(state.profilerColumns, state.searchId);
    });
  };

  const handleResetColumns = () => {
    runInAction(() => {
      state.profilerColumns = getInitialProfilerColumns(
        state.currentSearch,
        utilsStore.windowWidth,
        profilerAccessStore.isProfilerLiteOnly
      );
      // Reset also switches the Financials toggle off and forgets the selection
      // captured when it was last switched on.
      state.isProfilerExtendedFinancialsToggled = false;
      saveProfilerColumnsBySearchResultsPage(state.profilerColumns, state.searchId);
      saveProfilerExtendedFinancialsToggleBySearchResultsPage(false, state.searchId);
      localStorage.removeItem(`ppfosbsrp-${state.searchId}`);
    });
  };

  /**
   * Swaps the visible columns between the financial-only set and the analyst's own
   * selection, remembering that selection on the way in so it can be restored on
   * the way out. Falls back to "everything except the pure financial metrics" when
   * there is nothing stored.
   */
  const handleToggleFinancialsOnly = () => {
    const isOn = !state.isProfilerExtendedFinancialsToggled;

    runInAction(() => {
      state.isProfilerExtendedFinancialsToggled = isOn;
      saveProfilerExtendedFinancialsToggleBySearchResultsPage(isOn, state.searchId);

      if (isOn) {
        saveProfilerPreFinancialsOnlySelectionsBySearchResultsPage(
          state.profilerColumns.filter((column) => column.selected).map((column) => column.id),
          state.searchId
        );
      }

      const savedSelection = isOn
        ? null
        : getProfilerPreFinancialsOnlySelectionsBySearchResultsPage(state.searchId);
      const isSelected = resolveFinancialsOnlySelection(isOn, savedSelection);

      state.profilerColumns.forEach((column) => {
        column.selected = isSelected(column.id);
      });

      saveProfilerColumnsBySearchResultsPage(state.profilerColumns, state.searchId);
    });
  };

  // TD-2772 — `--anchored` carries the desktop dropdown's positioning and chrome.
  // The sheet-hosted copy simply does not get it, so there is nothing to reset: the
  // panel's interior is unscoped (`customize-columns.less`) and holds either way.
  const renderCustomizeColumnsMenu = ({ isMobile = false } = {}) => (
    <div
      className={`customize-columns-main-wrap${
        isMobile ? '' : ' customize-columns-main-wrap--anchored'
      } noselect`}
    >
      <div className="customize-columns-header">
        <div className="customize-columns-header-title">
          {
            state.profilerColumns.filter(
              (column) => column.id !== PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS && column.selected
            ).length
          }{' '}
          active columns
        </div>
      </div>
      <div className="customize-columns-separator"></div>
      <div className="customize-columns-list-main-wrap">
        <div className="customize-columns-list-wrap">
          <DndContext
            modifiers={[
              restrictToVerticalAxis,
              restrictToParentElement,
              restrictToFirstScrollableAncestor
            ]}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnReorder}
          >
            <SortableContext
              items={state.profilerColumns
                .filter((column) => column.id !== PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS)
                .map((column) => ({ id: column.id }))}
              strategy={verticalListSortingStrategy}
            >
              {state.profilerColumns
                .filter((column) => column.id !== PROFILER_SEARCH_RESULTS_COLUMNS.ACTIONS)
                .map((item) => (
                  <SortableColumnItem
                    key={item.id}
                    item={item}
                    onClick={() => handleToggleColumnSelected(item)}
                  />
                ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
      <div className="customize-columns-separator"></div>
      <div className="customize-columns-footer">
        <IconButton innerText="Reset" onClick={handleResetColumns} />
        <IconButton
          type={ICON_BUTTON_TYPES.BLUE}
          wrapClassName="customize-columns-financials-only-button"
          icon={
            state.isProfilerExtendedFinancialsToggled
              ? ICON_BUTTON_ICONS.TOGGLE_ON2
              : ICON_BUTTON_ICONS.TOGGLE_OFF2
          }
          innerText="Financials"
          onClick={(event) => {
            // Mobile only, as before: inside the bottom sheet the click would
            // otherwise reach the document listeners behind it. On desktop the
            // click is left to bubble, because that is what closes an open View
            // menu -- `toggleProfilerCustomizeColumns` does not close siblings.
            if (isMobile) event.stopPropagation();
            handleToggleFinancialsOnly();
          }}
          filled={state.isProfilerExtendedFinancialsToggled}
        />
      </div>
    </div>
  );

  // Shared so the profiles option is defined once rather than duplicated across
  // the two download popups (mobile + desktop). Gated on canExportProfilerOnepager
  // (Lite+Full, demo excluded — see TD-2705) and the 500-company profiles cap.
  // `onSelect` runs before the entry's action; the mobile sheet passes its close
  // (see renderProfilerDownloadPopup). Disabled entries never reach onClick.
  const renderProfilerCompanyProfilesButton = ({ onSelect = null } = {}) => {
    const isOverCap = exceedsListCap(state.sortedResults.length, DOWNLOAD_LIMIT_XLSX_PROFILES);
    const gate = profilerExportGate(
      profilerAccessStore.canExportProfilerOnepager,
      isDownloadLimitReached ||
        isSubscriptionLoading ||
        state.isLoading ||
        state.isInitialLoading ||
        isOverCap ||
        state.isDownloadingProfilerSearchProfilesList
    );
    return (
      <IconButton
        type={ICON_BUTTON_TYPES.BLUE}
        innerText={'Download company list as XLSX'}
        withBorder={false}
        onClick={() => {
          onSelect?.();
          requestBulkExport('profiles');
        }}
        disabled={gate.disabled}
        tooltipClickable={gate.tooltipClickable}
        tooltipContent={renderDownloadTooltip({
          hasAccess: gate.hasAccess,
          limit: DOWNLOAD_LIMIT_XLSX_PROFILES,
          isAllowanceReached: isDownloadLimitReached,
          isOverCap,
          isDownloading: state.isDownloadingProfilerSearchProfilesList,
          isSuccess: state.isSuccessDownloadProfilerSearchProfilesList,
          noAccessNode: gate.noAccessNode
        })}
        icon={downloadStateIcon({
          isDownloading: state.isDownloadingProfilerSearchProfilesList,
          isSuccess: state.isSuccessDownloadProfilerSearchProfilesList
        })}
        iconOnRight
      />
    );
  };

  const renderSearchOnepagerButton = ({ onSelect = null } = {}) => {
    const isOverCap = exceedsListCap(state.sortedResults.length, DOWNLOAD_LIMIT_PPTX_ONEPAGER);
    const gate = profilerExportGate(
      profilerAccessStore.canExportProfilerOnepager,
      isDownloadLimitReached ||
        isSubscriptionLoading ||
        state.isLoading ||
        state.isInitialLoading ||
        isOverCap ||
        state.isDownloadingProfilerSearchOnepagerList
    );
    return (
      <IconButton
        type={ICON_BUTTON_TYPES.BLUE}
        innerText={'Download onepager list as PPTX'}
        withBorder={false}
        onClick={() => {
          onSelect?.();
          requestBulkExport('onepager');
        }}
        disabled={gate.disabled}
        tooltipClickable={gate.tooltipClickable}
        tooltipContent={renderDownloadTooltip({
          hasAccess: gate.hasAccess,
          limit: DOWNLOAD_LIMIT_PPTX_ONEPAGER,
          isAllowanceReached: isDownloadLimitReached,
          isOverCap,
          isDownloading: state.isDownloadingProfilerSearchOnepagerList,
          isSuccess: state.isSuccessDownloadProfilerSearchOnepagerList,
          noAccessNode: gate.noAccessNode
        })}
        icon={downloadStateIcon({
          isDownloading: state.isDownloadingProfilerSearchOnepagerList,
          isSuccess: state.isSuccessDownloadProfilerSearchOnepagerList
        })}
        iconOnRight
      />
    );
  };

  const renderSearchContactsButton = ({ onSelect = null } = {}) => {
    const isOverCap = exceedsListCap(state.sortedResults.length, DOWNLOAD_LIMIT_XLSX_CONTACTS);
    const gate = profilerExportGate(
      profilerAccessStore.canDownloadProfilerContactsList,
      isDownloadLimitReached ||
        isSubscriptionLoading ||
        state.isLoading ||
        state.isInitialLoading ||
        isOverCap ||
        state.isDownloadingProfilerSearchContactList
    );
    return (
      <IconButton
        type={ICON_BUTTON_TYPES.BLUE}
        innerText={'Download contact list as XLSX'}
        withBorder={false}
        onClick={() => {
          onSelect?.();
          contactsDownload.requestDownload();
        }}
        disabled={gate.disabled}
        tooltipClickable={gate.tooltipClickable}
        tooltipContent={renderDownloadTooltip({
          hasAccess: gate.hasAccess,
          limit: DOWNLOAD_LIMIT_XLSX_CONTACTS,
          isAllowanceReached: isDownloadLimitReached,
          isOverCap,
          isDownloading: state.isDownloadingProfilerSearchContactList,
          isSuccess: state.isSuccessDownloadProfilerSearchContactList,
          noAccessNode: gate.noAccessNode
        })}
        icon={downloadStateIcon({
          isDownloading: state.isDownloadingProfilerSearchContactList,
          isSuccess: state.isSuccessDownloadProfilerSearchContactList
        })}
        iconOnRight
      />
    );
  };

  // Sheet-hosted twin of `renderDesktopDownloadPopup`; see the `--in-sheet`
  // note on the Customize menu above (TD-2772). Picking an entry closes the
  // sheet first: it is a native <dialog> in the top layer, so anything opened
  // after it that is not itself a native dialog (the enrichment contacts dialog,
  // the header error message of a direct download) would sit under its backdrop
  // (TD-3040).
  const renderProfilerDownloadPopup = () => {
    const sheetSelect = { onSelect: state.closeProfilerDownloadSearch };
    return (
      <div className="download-search-popup download-search-popup--in-sheet">
        <SubscriptionDownloadCounter label="Downloads" />
        <EnrichmentCreditCounter variant="row" />
        <div className="buttons-wrap">
          {renderSearchOnepagerButton(sheetSelect)}
          {renderProfilerCompanyProfilesButton(sheetSelect)}
          {renderSearchContactsButton(sheetSelect)}
        </div>
      </div>
    );
  };

  // TD-2764: sheet-body option rows for the mobile View menu (Option-B bottom
  // sheet). No `.view-menu-wrap` — the anchored-dropdown wrapper — because these
  // now render inside MobileSheet's scrollable body.
  const renderProfilerViewOptions = () => (
    <div className="mobile-sheet-options">
      {PROFILER_VIEWS_LIST.slice()
        .filter((view) =>
          // TD-2499: hide the (EBIT-centric) Chart view for Lite, and on narrow screens.
          utilsStore.windowWidth < 600 || profilerAccessStore.isProfilerLiteOnly
            ? view.value !== PROFILER_VIEWS.CHART
            : true
        )
        .map((view) => {
          return (
            <IconButton
              key={view.id}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={view.name}
              onClick={() => state.setProfilerView(view.value) & state.toggleProfilerViewMenu()}
              disabled={state.profilerView === view.value || !!view.disabled}
              filled={state.profilerView === view.value}
              {...(view.icon ? { iconOnRight: true, icon: view.icon } : {})}
              tooltipText={view.tooltipText || ''}
            />
          );
        })}
    </div>
  );

  // TD-2764: sheet-body option rows for the mobile Sort menu (Option-B bottom sheet).
  const renderProfilerSortOptions = () => (
    <div className="mobile-sheet-options">
      {/* TD-1718: Filter mobile sort options by visible columns (matching desktop) */}
      {state.profilerSortOptions
        .filter((option) =>
          state.profilerView === PROFILER_VIEWS.CHART
            ? isSortOptionVisibleInChart(option, state.selectedSort)
            : !option.onlyForColumn || // No column restriction OR
              !!state.profilerColumns.find(
                (column) => column.id === option.onlyForColumn && column.selected
              )
        )
        .map((option) => {
          return (
            <IconButton
              key={option.value}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={option.shortName || option.name}
              onClick={() => state.selectSortOption(option.value, true) & state.toggleSort()}
              disabled={state.effectiveSort === option.value}
              filled={state.effectiveSort === option.value}
              {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
            />
          );
        })}
    </div>
  );

  // TD-2764: the mobile Profiler Save / Download / View / Sort buttons were
  // retired here — those actions now live in the bottom `ResultsMobileActionBar`
  // (see `renderSearchMobileActionBar`). The desktop equivalents remain in the
  // `renderDesktop*` block below.

  // Labels the desktop Profiler sort button with the sort the results are really
  // in — `effectiveSort`, so Chart view names its EBIT-margin default. Early
  // returns rather than a nested ternary (S3358). (The mobile sort control is now
  // the bottom action bar's Sort sheet.)
  const profilerSortTooltipText = () => {
    if (
      state.toggledProfilerDownloadSearch ||
      state.isProfilerViewMenuToggled ||
      state.isSortToggled
    )
      return '';
    if (isLoading || !hasResults) return 'Sort';
    if (state.effectiveSort === 'default') return 'Sort';
    return state.profilerSortOptions.find((o) => o.value === state.effectiveSort)?.name || 'Sort';
  };

  const renderStandardSaveSearchButton = () => (
    <IconButton
      icon={ICON_BUTTON_ICONS.FLOPPY_2_FILL}
      type={ICON_BUTTON_TYPES.BLUE}
      filled={fillSaveSearchIcon}
      onClick={() => {
        if (canSaveSearch && !state.isSavingSearch) {
          state.toggleSaveProjectPopup(true);
        }
      }}
      disabled={disableSaveSearch || state.isSavingSearch}
      tooltipText={state.projectId ? 'Search already saved as project' : 'Save search'}
      highlightButtonBackground={
        !fillSaveSearchIcon && state.isAnyOutreachButtonHovered && !isOutreachDisabled
      }
    />
  );

  const renderStandardOutreachButton = () => (
    <IconButton
      onMouseEnter={() => state.setIsAnyOutreachButtonHovered(true)}
      onMouseLeave={() => state.setIsAnyOutreachButtonHovered(false)}
      icon={ICON_BUTTON_ICONS.CHAT_RIGHT_DOTS}
      type={ICON_BUTTON_TYPES.BLUE}
      tooltipText={
        state.isSortToggled
          ? ''
          : isOutreachDisabled
            ? 'Upcoming feature'
            : !state.projectId
              ? 'Save search results to initiate mass outreach'
              : 'Mass outreach'
      }
      tooltipPosition={!isOutreachDisabled && !state.projectId ? 'bottom-end' : 'bottom-center'}
      disabled={isOutreachDisabled || disableMassOutreach}
      onClick={() => state.toggleMassOutreachPopup(true)}
    />
  );

  const renderStandardDownloadButton = () => (
    <IconButton
      icon={ICON_BUTTON_ICONS.DOWNLOAD}
      type={ICON_BUTTON_TYPES.BLUE}
      disabled={!canDownload || !authStore.userInfo.isCognitoExportSearch}
      onClick={() => {
        if (authStore.userInfo.isCognitoExportSearch) {
          eventDownloadSearch();
          window.open(
            `${import.meta.env.VITE_API_BASEURL}/public/downloadSearch?searchId=${state.searchId}`,
            '_blank',
            'noopener, noreferrer'
          );
        }
      }}
      tooltipClickable
      tooltipContent={
        authStore.userInfo.isCognitoExportSearch ? (
          <>Download search</>
        ) : (
          <>
            Download feature is disabled.
            <br />
            Please contact us:{' '}
            <a href="mailto:support@transact.digital">support@transact.digital</a>
          </>
        )
      }
      tooltipPosition={authStore.userInfo.isCognitoExportSearch ? 'bottom-center' : 'bottom-end'}
    />
  );

  const renderStandardSortButton = () => (
    <IconButton
      id="sort-btn-mobile"
      icon={
        state.selectedSort !== 'default'
          ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
          : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
      }
      type={ICON_BUTTON_TYPES.BLUE}
      tooltipText={
        state.isSortToggled
          ? ''
          : !isLoading && hasResults
            ? state.selectedSort === 'default'
              ? 'Sort'
              : agentSearchSortOptions.find((o) => o.value === state.selectedSort)?.name || 'Sort'
            : 'Sort'
      }
      tooltipPosition={state.selectedSort !== 'default' ? 'bottom-end' : 'bottom-center'}
      disabled={isLoading || !hasResults}
      onClick={state.toggleSort}
      filled={state.isSortToggled || state.selectedSort !== 'default'}
    />
  );

  const renderDesktopSortMenu = () => (
    <div className="sort-menu-wrap">
      {state.profilerSortOptions
        .filter((option) =>
          state.profilerView === PROFILER_VIEWS.CHART
            ? isSortOptionVisibleInChart(option, state.selectedSort)
            : utilsStore.windowWidth >= 1366
              ? !option.onlyForColumn ||
                !!profilerColumns.find(
                  (column) => column.id === option.onlyForColumn && column.selected
                )
              : true
        )
        .map((option) => {
          return (
            <IconButton
              key={option.value}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={option.shortName || option.name}
              onClick={() => state.selectSortOption(option.value, true) & state.toggleSort()}
              disabled={state.effectiveSort === option.value}
              filled={state.effectiveSort === option.value}
              {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
            />
          );
        })}
    </div>
  );

  const renderDesktopDownloadPopup = () => (
    <div className="download-search-popup">
      <SubscriptionDownloadCounter />
      <div className="buttons-wrap">
        {renderSearchOnepagerButton()}
        {renderProfilerCompanyProfilesButton()}
        {renderSearchContactsButton()}
      </div>
    </div>
  );

  const renderDesktopListViewButton = () => (
    <IconButton
      icon={ICON_BUTTON_ICONS.FLOPPY_2_FILL}
      type={ICON_BUTTON_TYPES.BLUE}
      filled={fillSaveSearchIcon}
      wrapClassName="filter-button"
      onClick={() => {
        if (canSaveSearch && !state.isProfilerSavingSearch) {
          state.toggleProfilerSaveProjectPopup(true);
        }
      }}
      disabled={disableProfilerSaveSearch || state.isProfilerSavingSearch}
      tooltipClickable={!!state.projectId}
      tooltipContent={
        state.toggledProfilerDownloadSearch ||
        state.isProfilerViewMenuToggled ||
        state.isSortToggled ? null : state.projectId ? (
          <>
            Search already saved as project
            <br />
            <Link
              onClick={() => localStorage.setItem('highlighted-project', state.projectId)}
              to={savedProfilerProjectUrl(authStore.userAccountType, state.projectId)}
            >
              {profilerProjectStore.allProjects.find((p) => p.id === state.projectId)?.name ||
                state.projectId}
              &emsp;&#10230;
            </Link>
          </>
        ) : (
          <>Save search</>
        )
      }
      innerText="Save search"
      highlightButtonBackground={
        !fillSaveSearchIcon &&
        state.isDownloadProfilerButtonHovered &&
        !isProfilerSearchDownloadDisabled
      }
    />
  );

  const renderDesktopViewToggleButton = () => (
    <IconButton
      id="list-change-btn-desktop"
      wrapClassName="filter-button-no-text"
      icon={PROFILER_VIEWS_LIST.find((view) => view.value === state.profilerView).icon}
      type={ICON_BUTTON_TYPES.BLUE}
      tooltipText={
        state.toggledProfilerDownloadSearch ||
        state.isProfilerViewMenuToggled ||
        state.isSortToggled
          ? ''
          : 'Switch view'
      }
      disabled={isLoading || !hasResults}
      onClick={state.toggleProfilerViewMenu}
      filled={state.isProfilerViewMenuToggled}
    />
  );

  const renderDesktopViewMenu = () => (
    <div className="view-menu-wrap">
      {PROFILER_VIEWS_LIST
        // TD-2499: hide the (EBIT-centric) Chart view for Lite.
        .filter(
          (view) => !profilerAccessStore.isProfilerLiteOnly || view.value !== PROFILER_VIEWS.CHART
        )
        .map((view) => {
          return (
            <IconButton
              key={view.id}
              hoverType={ICON_BUTTON_TYPES.BLUE}
              withBorder={false}
              innerText={view.name}
              onClick={() => state.setProfilerView(view.value) & state.toggleProfilerViewMenu()}
              disabled={state.profilerView === view.value || !!view.disabled}
              filled={state.profilerView === view.value}
              {...(view.icon ? { iconOnRight: true, icon: view.icon } : {})}
              tooltipText={view.tooltipText || ''}
            />
          );
        })}
    </div>
  );

  const renderDesktopSortButtonDesktop = () => (
    <IconButton
      id="sort-btn-desktop"
      wrapClassName="filter-button-no-text"
      icon={
        state.effectiveSort !== 'default'
          ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
          : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
      }
      type={ICON_BUTTON_TYPES.BLUE}
      tooltipText={profilerSortTooltipText()}
      tooltipPosition={
        utilsStore.windowWidth <= 1200 && state.effectiveSort !== 'default'
          ? 'bottom-end'
          : 'bottom-center'
      }
      disabled={isLoading || !hasResults}
      onClick={state.toggleSort}
      filled={state.isSortToggled || state.effectiveSort !== 'default'}
    />
  );

  const renderDesktopDownloadButtonDesktop = () => (
    <IconButton
      id="dps-btn-desktop"
      onMouseEnter={() => state.setIsDownloadProfilerButtonHovered(true)}
      onMouseLeave={() => state.setIsDownloadProfilerButtonHovered(false)}
      icon={ICON_BUTTON_ICONS.DOWNLOAD}
      type={ICON_BUTTON_TYPES.BLUE}
      wrapClassName="filter-button-no-text"
      onClick={state.toggleProfilerDownloadSearch}
      filled={state.toggledProfilerDownloadSearch}
      tooltipPosition={
        utilsStore.windowWidth <= 1200 && !isProfilerSearchDownloadDisabled && !state.projectId
          ? 'bottom-end'
          : 'bottom-center'
      }
      tooltipContent={(() => {
        const tooltip = getProfilerDownloadTriggerTooltip({
          menuOpen: state.toggledProfilerDownloadSearch,
          viewMenuOpen: state.isProfilerViewMenuToggled,
          sortOpen: state.isSortToggled,
          downloadFeatureDisabled: isProfilerSearchDownloadDisabled,
          projectId: state.projectId
        });
        return tooltip ? <>{tooltip}</> : null;
      })()}
      disabled={isProfilerDownloadTriggerDisabled({
        downloadFeatureDisabled: isProfilerSearchDownloadDisabled,
        canDownload,
        projectId: state.projectId
      })}
    />
  );

  const renderStandardHeaderSaveButton = () => (
    <IconButton
      icon={ICON_BUTTON_ICONS.FLOPPY_2_FILL}
      type={ICON_BUTTON_TYPES.BLUE}
      filled={fillSaveSearchIcon}
      onClick={() => {
        if (canSaveSearch && !state.isSavingSearch) {
          state.toggleSaveProjectPopup(true);
        }
      }}
      disabled={disableSaveSearch || state.isSavingSearch}
      tooltipText={
        state.isSortToggled
          ? ''
          : state.projectId
            ? 'Search already saved as project'
            : 'Save search'
      }
      highlightButtonBackground={
        !fillSaveSearchIcon && state.isAnyOutreachButtonHovered && !isOutreachDisabled
      }
    />
  );

  const renderStandardHeaderOutreachButton = () => (
    <IconButton
      onMouseEnter={() => state.setIsAnyOutreachButtonHovered(true)}
      onMouseLeave={() => state.setIsAnyOutreachButtonHovered(false)}
      icon={ICON_BUTTON_ICONS.CHAT_RIGHT_DOTS}
      type={ICON_BUTTON_TYPES.BLUE}
      tooltipText={
        state.isSortToggled
          ? ''
          : isOutreachDisabled
            ? 'Upcoming feature'
            : !state.projectId
              ? 'Save search results to initiate mass outreach'
              : 'Mass outreach'
      }
      tooltipPosition={
        utilsStore.windowWidth > 1200
          ? 'bottom-center'
          : isOutreachDisabled
            ? 'bottom-center'
            : !state.projectId
              ? 'bottom-end'
              : 'bottom-center'
      }
      disabled={isOutreachDisabled || disableMassOutreach}
      onClick={() => state.toggleMassOutreachPopup(true)}
    />
  );

  const renderStandardHeaderDownloadButton = () => (
    <IconButton
      icon={ICON_BUTTON_ICONS.DOWNLOAD}
      type={ICON_BUTTON_TYPES.BLUE}
      disabled={!canDownload || !authStore.userInfo.isCognitoExportSearch}
      onClick={() => {
        if (authStore.userInfo.isCognitoExportSearch) {
          eventDownloadSearch();
          window.open(
            `${import.meta.env.VITE_API_BASEURL}/public/downloadSearch?searchId=${state.searchId}`,
            '_blank',
            'noopener, noreferrer'
          );
        }
      }}
      tooltipClickable
      tooltipContent={
        state.isSortToggled ? null : authStore.userInfo.isCognitoExportSearch ? (
          <>Download search</>
        ) : (
          <>
            Download feature is disabled.
            <br />
            Please contact us:{' '}
            <a href="mailto:support@transact.digital">support@transact.digital</a>
          </>
        )
      }
      tooltipPosition={authStore.userInfo.isCognitoExportSearch ? 'bottom-center' : 'bottom-end'}
    />
  );

  const renderStandardHeaderSortButton = () => (
    <IconButton
      id="sort-btn-desktop"
      icon={
        state.selectedSort !== 'default'
          ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
          : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
      }
      type={ICON_BUTTON_TYPES.BLUE}
      tooltipText={
        state.isSortToggled
          ? ''
          : !isLoading && hasResults
            ? state.selectedSort === 'default'
              ? 'Sort'
              : agentSearchSortOptions.find((o) => o.value === state.selectedSort)?.name || 'Sort'
            : 'Sort'
      }
      tooltipPosition={
        utilsStore.windowWidth <= 1200 && state.selectedSort !== 'default'
          ? 'bottom-end'
          : 'bottom-center'
      }
      disabled={isLoading || !hasResults}
      onClick={state.toggleSort}
      filled={state.isSortToggled || state.selectedSort !== 'default'}
    />
  );

  const renderStandardHeaderSortMenu = () => (
    <div className="sort-menu-wrap">
      {state.agentSortOptions.map((option) => {
        return (
          <IconButton
            key={option.value}
            hoverType={ICON_BUTTON_TYPES.BLUE}
            withBorder={false}
            innerText={option.shortName || option.name}
            onClick={() => state.selectSortOption(option.value) & state.toggleSort()}
            disabled={state.selectedSort === option.value}
            filled={state.selectedSort === option.value}
            {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
          />
        );
      })}
    </div>
  );

  const renderPopups = () => (
    <>
      <BulkExportConfirmModal
        isOpen={bulkExportPendingType !== null}
        totalCompanies={state.sortedResults?.length ?? 0}
        downloads={downloads}
        period={contractPeriod}
        source="project"
        sourceId={state.projectId}
        includeContacts={precheckIncludesContacts(bulkExportPendingType)}
        prefillData={bulkExportPrefillData}
        onConfirm={confirmBulkExport}
        onCancel={cancelBulkExport}
      />
      {showDetailsPopup && (
        <DetailsPopup
          searchType={state.searchType}
          detailsPopupDisplayed={state.detailsPopupDisplayed}
          detailsPopupSelectedTab={state.detailsPopupSelectedTab}
          setDetailsPopupSelectedTab={state.setDetailsPopupSelectedTab}
          displayDetailsPopup={state.displayDetailsPopup}
        />
      )}
      {showMessagePopup && (
        <MessagePopup
          messagePopupDisplayed={state.messagePopupDisplayed}
          displayMessagePopup={state.displayMessagePopup}
          submitMessage={state.onSubmitMessage}
          actionObj={
            state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Funds
              ? state.fundsInAction
              : state.searchType[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE]['Portfolio Co.']
                ? state.portfoliosInAction
                : state.investProfilesInAction
          }
          forSearchType={state.searchType[0]}
        />
      )}
      {showMassOutreachPopup && (
        <MassOutreachPopup
          toggleMassOutreachPopup={state.toggleMassOutreachPopup}
          submitMessage={state.onMassOutreach}
          projectId={state.projectId}
          entries={state.results.filter((r) => !r.isExcluded)}
        />
      )}
      {showSaveSearchPopup && (
        <SaveSearchPopup
          toggleSaveProjectPopup={state.toggleSaveProjectPopup}
          onSaveSearch={state.onSaveSearch}
          projectId={state.projectId}
        />
      )}
      {showProfilerSaveSearchPopup && (
        <ProfilerSaveSearchPopup
          toggleSaveProjectPopup={state.toggleProfilerSaveProjectPopup}
          onSaveSearch={state.onProfilerSaveSearch}
          projectId={state.projectId}
        />
      )}

      <ProjectContactsDownloadDialog
        isOpen={contactsDownload.isOpen}
        onClose={contactsDownload.close}
        projectId={state.projectId}
        companyCount={state.sortedResults.length}
      />
    </>
  );

  return (
    <StaticPageLayout page="search-listing" hideMobileFooter={false}>
      {state.searchType?.[0] === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler && (
        <DataSourceFloatingLegend />
      )}
      {renderPopups()}
      {state.isMobileFiltersDisplayed && (
        <MobileFilters
          state={state}
          bulkAdjust={bulkAdjustProps}
          fundsInvestmentCriteriaFields={fundsInvestmentCriteriaFields}
          portfoliosInvestmentCriteriaFields={portfoliosInvestmentCriteriaFields}
          profilerCompanyCriteriaFields={profilerCompanyCriteriaFields}
          profilerIndustryCodesFields={profilerIndustryCodesFields}
        />
      )}
      {!state.isMobileFiltersDisplayed && (
        <div className="listing-layout">
          <div className="results">
            {renderMobileToolbar()}

            {renderDesktopSearchFilters()}

            {currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
              renderProfilerResultsHeader()}
            {/* TD-2456 — mobile pill-filter rail. `.results-header` (and the
               desktop rail inside it) is display:none on mobile, so render the
               mobile variant here, outside the header, where it stays visible. */}
            {currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
              (isUnifiedFiltersEnabled ? (
                <UnifiedChipRail store={state} variant="mobile" visible={isPillRailVisible} />
              ) : (
                <PillFilterRail store={state} variant="mobile" visible={isPillRailVisible} />
              ))}
            {currentSearchType !== UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
              renderStandardResultsHeader()}

            {/* TD-2377 Phase 3 Part B — the results-body region (loaders, error,
               empty state, Profiler chart/heatmap, the paginated list, demo
               banner, excluded panel and pager) moved to <SearchResultsBody/> to
               lift its JSX conditional gates out of this component's own body. */}
            <SearchResultsBody
              state={state}
              isLoading={isLoading}
              hasResults={hasResults}
              hasError={hasError}
              showNoResults={showNoResults}
              showResults={showResults}
              showPaging={showPaging}
              currentSearchType={currentSearchType}
              excludedResults={excludedResults}
              profilerColumns={profilerColumns}
              profilerColumnHeaderJSX={profilerColumnHeaderJSX}
              profilerSearchLoader={profilerSearchLoader}
              navigateToCompanyDetails={navigateToCompanyDetails}
              navigate={navigate}
              location={location}
              authStore={authStore}
              profilerAccessStore={profilerAccessStore}
            />
            {/* TD-2764: mobile Profiler bottom action bar. Rendered last in
                `.results` so its in-flow spacer clears the final list row; the
                bar itself portals to <body> and is CSS-hidden ≥834px. */}
            {currentSearchType === UI_OPTIONS[UI_OPTION_KEYS.SEARCH_TYPE].Profiler &&
              renderSearchMobileActionBar()}
          </div>
        </div>
      )}
    </StaticPageLayout>
  );
});

export default SearchResults;
